/**
 * VideoCallPageV2 — Premium Teleconsulta with AI medical notes
 * Layout v5: Header (60px) + Body (col-main + col-side)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  AlertTriangle, Aperture, ArrowLeft, AudioLines, Ban, Bell, BookOpen, Captions, CaptionsOff, Check, ChevronDown, ChevronUp, ClipboardList, Copy, CornerUpLeft, Eye, Heart, Image, Loader2, MapPin, MessageSquareText, Mic, MicOff, MonitorX, Moon, PanelLeftClose, PanelLeftOpen, Phone, PhoneOff, PhoneOutgoing, Pill, Plus, RefreshCw, Save, Send, ShieldAlert, Sparkles, Stethoscope, TextSelect, TimerReset, User, Video, VideoOff, WifiOff, X,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, orderBy, writeBatch, doc, onSnapshot, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { functions, db, auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDocument } from '../hooks/useFirestore';
import { CloseModal, Modal, Button } from '../components/ui';
import { type BackgroundOption, clearBackgroundCache } from '../hooks/backgroundCatalog';
import { useAdminBackgrounds } from '../hooks/useAdminBackgrounds';
import { VirtualBgApplier } from '../components/VirtualBgApplier';
import { useTranscription, type TranscriptEntry } from '../hooks/useTranscription';
import { useMedicalNotes } from '../hooks/useMedicalNotes';
import { usePlanCuration } from '../hooks/usePlanCuration';
import { useLiveSoap } from '../hooks/useLiveSoap';
import { useTranscriptPersist } from '../hooks/useTranscriptPersist';
import { useClinicalGuard } from '../hooks/useClinicalGuard';
import { useSoapMerge } from '../hooks/useSoapMerge';
import { useSpeechToText } from '../hooks/useSpeechToText';
import { ReconnectionOverlay } from '../components/livekit/ReconnectionOverlay';
import { AvisosConexion } from '../components/livekit/AvisosConexion';
import type { NivelAviso } from '../hooks/avisoConexion';
import { useSalaExclusiva } from '../hooks/useSalaExclusiva';
import { PacienteNoContestaBanner } from '../components/livekit/PacienteNoContestaBanner';
import { PacienteColgoBanner } from '../components/livekit/PacienteColgoBanner';
import { HIPAAConsentModal } from '../components/livekit/HIPAAConsentModal';
import { IndicadorGrabacion } from '../components/livekit/IndicadorGrabacion';
import { StructuredPlan } from '../components/livekit/StructuredPlan';
import { NotaSoape } from '../components/livekit/NotaSoape';
import { EditableText } from '../components/livekit/EditableText';
import { ExpedienteTab } from '../components/livekit/ExpedienteTab';
import { Avatar } from '../components/ui/Avatar';
import { useSessionDeadline, formatRemaining } from '../hooks/useSessionDeadline';
import { extenderSesion, aceptarConsulta as aceptarConsultaSvc, iniciarGrabacion } from '../services/consultaService';
import { getDoctorBgPreference, saveDoctorBgPreference } from '../services/backgroundService';
import { logClient } from '../services/obsClientLogger';
import { TranscriptionCollector } from '../components/TranscriptionCollector';
// BrowserSpeechBridge deshabilitado — transcripción via agente LiveKit únicamente
// import { BrowserSpeechBridge } from '../components/BrowserSpeechBridge';
import {
  LiveKitRoom, VideoTrack, AudioTrack,
  useRemoteParticipants, useTracks, useConnectionState,
  useLocalParticipant,
} from '@livekit/components-react';
import { Track, ConnectionState, VideoPresets, DisconnectReason } from 'livekit-client';
import { useAtributoParticipante } from '../hooks/useAtributoParticipante';
import type { LocalAudioTrack, RoomOptions, RoomConnectOptions } from 'livekit-client';
import { useNoiseCancellation } from '../hooks/useNoiseCancellation';
import type { Consulta, DisposicionConsulta } from '../types';
import type { PatientContext } from '../services/medicalAI';
import { copiarSoape, copiarSeleccionActual } from '../utils/copiarSoape';
import '../styles/notaClinica.css';
import styles from './VideoCallPageV2.module.css';

/* ── Types ── */

interface LiveKitTokenResult {
  token: string;
  url: string;
  room: string;
  identity: string;
  esMedico: boolean;
  /** Política de ICE resuelta en el servidor ('all' salvo que se fuerce relay). */
  iceTransportPolicy?: RTCIceTransportPolicy;
  /**
   * El backend probó el servidor de video al preparar la sala y no respondió.
   * El token se entrega igual —una caída de un instante se resuelve sola con
   * los reintentos del cliente— pero conviene explicar el "Conectando…" en vez
   * de dejar al médico esperando sin saber por qué.
   */
  advertencia?: { motivo: 'fuera_de_horario' | 'servidor_no_responde'; texto: string } | null;
}

// Estructura vacía cuando la consulta aún no se ha cargado de Firestore.
const EMPTY_PATIENT = {
  nombre: '',
  cedula: '',
  salaId: '',
  salaNombre: '',
  motivoConsulta: '',
} as const;

/** ¿El identificador parece una cédula/pasaporte y NO un Firebase UID? */
function pareceCedulaStr(id?: string): boolean {
  return !!id && (/-/.test(id) || /^[0-9]{5,12}$/.test(id));
}

function calcularEdad(fechaNacimiento: string | null | undefined): number {
  if (!fechaNacimiento) return 0;
  const birth = new Date(fechaNacimiento);
  if (isNaN(birth.getTime())) return 0;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age > 0 ? age : 0;
}

/** Convierte un timestamp de Firestore (Timestamp, {_seconds}, ISO, Date) a ms. */
function tsToMillis(ts: unknown): number {
  if (!ts) return 0;
  if (typeof ts === 'object') {
    const o = ts as { _seconds?: number; seconds?: number; toMillis?: () => number };
    if (typeof o.toMillis === 'function') return o.toMillis();
    if (typeof o._seconds === 'number') return o._seconds * 1000;
    if (typeof o.seconds === 'number') return o.seconds * 1000;
  }
  const d = new Date(ts as string | number);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

/** Normaliza el género (códigos FHIR de SEIS/HCIS o texto libre) a español. */
function mapGenero(g: unknown): string {
  const v = String(g ?? '').trim().toLowerCase();
  if (v === 'male' || v === 'm' || v === 'masculino' || v === 'hombre') return 'Masculino';
  if (v === 'female' || v === 'f' || v === 'femenino' || v === 'mujer') return 'Femenino';
  if (v === 'other' || v === 'otro' || v === 'o') return 'Otro';
  if (!v || v === 'unknown' || v === 'desconocido') return '';
  // Cualquier otro valor ya legible se muestra capitalizado.
  return g as string;
}

function buildPatientContext(
  consulta: { pacienteNombre?: string; pacienteEdad?: number | null; pacienteSexo?: string | null } | null | undefined,
  pacienteData?: Record<string, unknown> | null,
  fhirData?: Record<string, unknown> | null,
  pdpData?: Record<string, unknown> | null,
): PatientContext {
  const edadFromPaciente = pacienteData?.fechaNacimiento
    ? calcularEdad(pacienteData.fechaNacimiento as string)
    : 0;
  const edadFromConsulta = typeof consulta?.pacienteEdad === 'number' ? consulta.pacienteEdad : 0;
  // M4: edad/sexo desde el gateway MINSA (api/paciente → caracterSexo/
  // fechaNacimiento) y, como último recurso, desde FHIR.
  const edadFromPdp = pdpData?.fechaNacimiento
    ? calcularEdad(pdpData.fechaNacimiento as string)
    : 0;
  const edadFromFhir = fhirData?.fechaNacimiento
    ? calcularEdad(fhirData.fechaNacimiento as string)
    : 0;

  return {
    name: consulta?.pacienteNombre ?? '',
    age: edadFromPaciente || edadFromConsulta || edadFromPdp || edadFromFhir,
    // Fallback en cadena: registro del portal → sexo enviado por la app móvil →
    // gateway MINSA (caracterSexo) → género del API FHIR.
    gender:
      mapGenero(pacienteData?.sexo) ||
      mapGenero(consulta?.pacienteSexo) ||
      mapGenero(pdpData?.caracterSexo) ||
      mapGenero(fhirData?.genero),
    allergies: Array.isArray(pacienteData?.alergias) ? pacienteData.alergias as string[] : [],
    medications: Array.isArray(pacienteData?.medicamentosActuales) ? pacienteData.medicamentosActuales as string[] : [],
    conditions: Array.isArray(pacienteData?.condicionesCronicas) ? pacienteData.condicionesCronicas as string[] : [],
    familyHistory: Array.isArray(pacienteData?.antecedentesFamiliares) ? pacienteData.antecedentesFamiliares as string[] : [],
    personalHistory: Array.isArray(pacienteData?.antecedentesPersonales) ? pacienteData.antecedentesPersonales as string[] : [],
  };
}

// Used by CloseModal for disposition selection
// eslint-disable-next-line react-refresh/only-export-components
export const DISPOSITION_OPTIONS: { value: DisposicionConsulta; label: string }[] = [
  { value: 'completada', label: 'Consulta completada' },
  { value: 'paciente_no_presento', label: 'Paciente no se presentó' },
  { value: 'paciente_rechazo', label: 'Paciente rechazó la llamada' },
  { value: 'fallo_tecnico', label: 'Fallo técnico' },
  { value: 'cancelada_medico', label: 'Cancelada por el médico' },
  { value: 'incompleta', label: 'Consulta incompleta' },
  { value: 'referida', label: 'Referida / Transferida' },
];

type RightTab = 'soap' | 'copilot' | 'patient' | 'expediente' | 'transcript';

/** Espacio reservado (mínimo del video + paddings/gaps/handles) al redimensionar
 *  los paneles, para que ninguna columna se salga de la pantalla. */
const PANEL_OVERHEAD = 420;

function getInitials(name: string): string {
  return name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')} : ${String(s).padStart(2, '0')}`;
}

/* ── LiveKit token: cache con TTL corto + invalidación por consultaId ── */
interface CachedToken { result: LiveKitTokenResult; cachedAt: number }
const tokenCache: Record<string, CachedToken> = {};
const TOKEN_CACHE_TTL_MS = 60_000;

/** Espera de estabilidad del contenido clínico antes de auto-generar el
 *  resumen para el paciente: el Plan aparece antes que los recursos FHIR. */
const ESPERA_CONTENIDO_ESTABLE_MS = 8_000;
/** Regeneración por cambios posteriores del plan: espera más que la primera
 *  vez, para no llamar a la IA en cada retoque. */
const ESPERA_REGENERAR_RESUMEN_MS = 20_000;

async function fetchLivekitToken(consultaId: string): Promise<LiveKitTokenResult> {
  const cached = tokenCache[consultaId];
  if (cached && Date.now() - cached.cachedAt < TOKEN_CACHE_TTL_MS) {
    console.log('[VideoCall] LiveKit token cache HIT', { consultaId });
    return cached.result;
  }
  console.log('[VideoCall] LiveKit token cache MISS — fetching fresh', { consultaId });
  const getLivekitToken = httpsCallable<{ consultaId: string }, LiveKitTokenResult>(
    functions,
    'portal_getLivekitToken',
  );
  const result = await getLivekitToken({ consultaId });
  console.log('[VideoCall] LiveKit token received', {
    consultaId,
    identity: result.data.identity,
    esMedico: result.data.esMedico,
  });
  tokenCache[consultaId] = { result: result.data, cachedAt: Date.now() };
  return result.data;
}

function invalidateLivekitTokenCache(consultaId: string): void {
  delete tokenCache[consultaId];
}

/* ── Sub-components ── */


/* ── Opciones de sala del MÉDICO ──────────────────────────────────────────
   Constantes de módulo a propósito: un objeto nuevo en cada render haría que
   LiveKitRoom reconstruya la Room y tumbe la llamada.

   Hasta ahora el portal no declaraba NINGUNA de estas opciones mientras la app
   del paciente sí las tenía. El resultado era asimétrico: frente a un paciente
   en red lenta, el que saturaba el enlace era el médico. Sin `adaptiveStream`
   se suscribía a resolución completa aunque el mosaico fuera pequeño, y sin
   `dynacast` publicaba todas las capas de simulcast aunque nadie las
   consumiera.

   Nota: la política ICE ya NO se fija aquí. Viaja en el token, resuelta desde
   `config/livekit.forzarRelay`, cuyo valor por defecto es `false` → 'all'. Este
   comentario decía lo contrario y contradecía al código de abajo, que usa el
   valor recibido: forzar el relay convertía al TURN en punto único de falla y
   se corrigió en 3f989e18. */
const OPCIONES_SALA: RoomOptions = {
  // Suscribirse solo a la calidad que el tamaño en pantalla realmente necesita.
  adaptiveStream: true,
  // No publicar capas que nadie está viendo.
  dynacast: true,
  publishDefaults: {
    simulcast: true,
    // La cámara del médico es un plano fijo de su cara: ante estrechez de
    // banda conviene sacrificar resolución antes que fluidez, porque un video
    // a tirones se percibe como "se cortó la llamada".
    degradationPreference: 'maintain-framerate',
  },
  // 540p es de sobra para el mosaico del paciente y reduce mucho la subida
  // del médico, que suele ser el enlace más caro de la sesión.
  videoCaptureDefaults: { resolution: VideoPresets.h540.resolution },
};

/**
 * La política de ICE la decide el SERVIDOR y llega con el token.
 *
 * Estaba fija en `'relay'`, lo que obligaba a que TODO el medio pasara por el
 * TURN y lo convertía en punto único de falla: en las alertas del 30-jul al
 * 3-ago el relay apareció caído en 13 de 15 revisiones, y con esa política no
 * conectaba ninguna llamada, ni siquiera entre dos equipos en la misma red.
 * Con `'all'`, ICE prueba la ruta directa y usa el TURN como respaldo — que es
 * para lo que existe.
 */
const opcionesConexion = (ice: RTCIceTransportPolicy): RoomConnectOptions => ({
  rtcConfig: { iceTransportPolicy: ice },
  // El default es 1: un solo reintento del join inicial. En una red con
  // pérdida, el primer intento falla con facilidad y el médico veía un error
  // que se resolvía recargando.
  maxRetries: 3,
});

/**
 * Resultado del aviso al paciente, tal como lo escribe el backend
 * (`pushTeleconsulta.avisarLlamadaEntrante`).
 */
export interface AvisoLlamada {
  estado?: 'enviando' | 'entregado' | 'fallido' | 'sin_dispositivos' | 'sin_app';
  dispositivos?: number;
  aceptados?: number;
  origen?: 'token_medico' | 'webhook' | 'reintento';
}

/**
 * Estado REAL del timbre al paciente.
 *
 * Antes aquí había un texto fijo, "Llamando al paciente…", que se mostraba
 * siempre que no hubiera nadie del otro lado. No significaba nada: se veía
 * idéntico si el push salió bien, si el paciente no tiene la app, o si nunca se
 * envió porque el servidor de video estaba caído. El médico esperaba en vano
 * sin forma de distinguir los tres casos — fue el síntoma que se reportó.
 *
 * Ahora cada estado dice lo que pasó y, cuando hay algo que hacer, qué hacer.
 */
function EstadoAviso({ aviso }: { aviso?: AvisoLlamada | null }) {
  // Sin dato todavía: el aviso viaja por Firestore y tarda un instante en
  // llegar. Mientras tanto, lo honesto es decir que se está avisando.
  if (!aviso || aviso.estado === 'enviando') {
    return (
      <span className={styles.audioFirstPill}>
        <PhoneOutgoing size={12} aria-hidden="true" /> Avisando al paciente…
      </span>
    );
  }

  if (aviso.estado === 'entregado') {
    return (
      <span className={styles.audioFirstPill}>
        <PhoneOutgoing size={12} aria-hidden="true" /> Llamando al paciente
        {aviso.dispositivos && aviso.dispositivos > 1
          ? ` · avisado en ${aviso.aceptados} de ${aviso.dispositivos} dispositivos`
          : ' · su teléfono está sonando'}
      </span>
    );
  }

  const mensaje = aviso.estado === 'sin_dispositivos'
    ? 'El paciente no tiene notificaciones activas — no se le pudo avisar'
    : aviso.estado === 'sin_app'
      ? 'Esta consulta no tiene app móvil asociada — no se le puede avisar'
      : 'No se pudo avisar al paciente';

  return (
    <span className={`${styles.audioFirstPill} ${styles.audioFirstPillMuted}`}>
      <PhoneOff size={12} aria-hidden="true" /> {mensaje}
    </span>
  );
}

function PatientVideo({ patientName, patientFotoURL, onAspectChange, onMicMutedChange, onSinSenalChange, avisoLlamada }: {
  patientName: string;
  patientFotoURL?: string | null;
  onAspectChange?: (ratio: number) => void;
  /** Notifica al padre si el paciente SILENCIÓ su micrófono (no si el track
   *  no llegó: eso es `onSinSenalChange`). */
  onMicMutedChange?: (muted: boolean) => void;
  /** El paciente publica cámara o micrófono sin mutear, pero el medio no está
   *  llegando. Es un problema de conexión, no un estado del paciente. */
  onSinSenalChange?: (sinSenal: boolean) => void;
  /** Resultado REAL del aviso al paciente (backend). Ver `AvisoLlamada`. */
  avisoLlamada?: AvisoLlamada | null;
}) {
  useRemoteParticipants();
  const containerRef = useRef<HTMLDivElement>(null);
  const onAspectRef = useRef(onAspectChange);
  useEffect(() => {
    onAspectRef.current = onAspectChange;
  }, [onAspectChange]);
  // Aspect ratio REAL del video remoto (< 1 = vertical, típico 9:16 del teléfono).
  const [aspect, setAspect] = useState(16 / 9);
  const tracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }, { source: Track.Source.Microphone, withPlaceholder: true }],
    { onlySubscribed: false },
  );
  // Sólo el track del PACIENTE — un supervisor observador (identidad
  // `supervisor_…`) no debe secuestrar el tile del paciente.
  const remoteTracks = tracks.filter(
    t => !t.participant.isLocal && (t.participant.identity ?? '').startsWith('paciente_'),
  );
  // Referencias por fuente SIN exigir `.track`: cuando LiveKit desuscribe un
  // track muteado, `publication.track` se vuelve undefined y aun así hay que
  // saber que la publicación existe (para estados de cámara/micrófono).
  const camRef = remoteTracks.find(t => t.source === Track.Source.Camera);
  const micRef = remoteTracks.find(t => t.source === Track.Source.Microphone);
  const remoteVideo = camRef?.publication?.track ? camRef : undefined;
  const remoteAudio = micRef?.publication?.track ? micRef : undefined;
  const pacientePresente = remoteTracks.length > 0;

  /* ── Apagado NO es lo mismo que "no me llegó" ──────────────────────────────
   * Antes, tanto la cámara como el micrófono se daban por apagados con solo
   * mirar si `publication.track` existía. Eso mezcla dos cosas distintas:
   *
   *   - el paciente lo apagó          → publication.isMuted === true
   *   - el track no está suscrito     → publication.track === undefined
   *
   * Lo segundo pasa continuamente sin que el paciente haya tocado nada:
   * mientras se restablece una reconexión (el 24-ago el médico salió y volvió
   * a entrar en 1 segundo), cuando `adaptiveStream` —que este portal activa—
   * pausa el video que no está visible, y sobre todo cuando la señalización
   * llega pero el medio no, que es justo lo que ocurre con el TURN caído.
   *
   * El resultado era que el médico veía "cámara apagada" y "micrófono
   * silenciado" mientras el paciente tenía ambos encendidos: la interfaz
   * afirmaba algo del paciente cuando en realidad era un problema de conexión.
   *
   * Ahora la única fuente de "apagado" es `isMuted`, y la falta de track con
   * la publicación viva y sin mutear se reporta aparte como falta de señal. */
  const camMuteada = camRef?.publication?.isMuted === true;
  const micMuteado = micRef?.publication?.isMuted === true;
  /* ¿La app del paciente apagó su cámara PORQUE su conexión no daba para video? Lo declara con
     el atributo `modoSoloAudio` (LiveKit propaga los atributos de participante solo). Sin esto
     su pausa se ve idéntica a «apagó la cámara» y el médico entiende una decisión donde hay un
     problema de red. Los builds anteriores al 195 no lo mandan: ahí se cae al texto de antes. */
  const pacienteEnSoloAudio =
    useAtributoParticipante((camRef ?? micRef)?.participant, 'modoSoloAudio') === '1';
  /* Publicado y sin mutear, pero el medio no llegó: es conexión, no el paciente.
   *
   * Se excluye el caso en que la pausa la provocamos nosotros: con
   * `adaptiveStream` LiveKit deshabilita la suscripción del video que no está
   * a la vista, y ahí `isEnabled` queda en false. Eso es funcionamiento
   * normal, no una falla que el médico deba atender. */
  const camPausadaPorNosotros = camRef?.publication?.isEnabled === false;
  const camSinSenal =
    !!camRef?.publication && !camMuteada && !camRef.publication.track && !camPausadaPorNosotros;
  const micSinSenal = !!micRef?.publication && !micMuteado && !micRef.publication.track;

  // Cámara realmente visible: hay track suscrito y no está muteada.
  const camLive = !!remoteVideo && !camMuteada;
  // Solo se afirma "silenciado" cuando el paciente lo silenció de verdad.
  const micMuted = pacientePresente && micMuteado;
  useEffect(() => { onMicMutedChange?.(micMuted); }, [micMuted, onMicMutedChange]);
  useEffect(() => { onSinSenalChange?.(pacientePresente && (camSinSenal || micSinSenal)); },
    [pacientePresente, camSinSenal, micSinSenal, onSinSenalChange]);

  // ¿El paciente YA se unió alguna vez en esta sesión? Sirve para distinguir el
  // estado inicial "Llamando al paciente…" (aún no ha entrado, se le está
  // llamando) del "Paciente desconectado" (entró y luego salió). Antes ambos se
  // mostraban como "Paciente desconectado", lo que confundía al médico al iniciar.
  const [pacienteYaEntro, setPacienteYaEntro] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { if (pacientePresente) setPacienteYaEntro(true); }, [pacientePresente]);

  // Detect actual video dimensions → report aspect ratio to parent
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const video = el.querySelector('video');
      if (video && video.videoWidth && video.videoHeight) {
        const ratio = video.videoWidth / video.videoHeight;
        setAspect(ratio);
        onAspectRef.current?.(ratio);
      }
    };
    // Poll until dimensions available, then use resize event
    const timer = setInterval(update, 500);
    const observer = new MutationObserver(() => {
      const video = el.querySelector('video');
      if (video) {
        video.addEventListener('resize', update);
        video.addEventListener('loadedmetadata', update);
        update();
      }
    });
    observer.observe(el, { childList: true, subtree: true });
    return () => { clearInterval(timer); observer.disconnect(); };
  }, []);

  const isVertical = aspect < 1;

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0 }}>
      {remoteAudio?.publication?.track && <AudioTrack trackRef={remoteAudio} volume={1} />}
      {/* Cámara apagada = track MUTEADO (LiveKit no lo despublica): sin el
          check de isMuted el <video> quedaba montado sin frames → recuadro
          negro en lugar del placeholder audio-primero. `camLive` también
          cubre track desuscrito (undefined) y publicación removida. */}
      {camLive && remoteVideo ? (
        isVertical ? (
          /* Video VERTICAL (9:16): marco centrado a altura completa con el video
             en cover + "alas ambientales" detrás — el MISMO track difuminado a
             pantalla completa. Las alas solo se montan en modo vertical
             (rendimiento): nada de columnas negras planas. */
          <>
            <div className={styles.stageWings} aria-hidden="true">
              <VideoTrack trackRef={remoteVideo} />
            </div>
            <div className={styles.stageFrame} style={{ aspectRatio: aspect }}>
              <VideoTrack trackRef={remoteVideo} />
            </div>
          </>
        ) : (
          /* Video HORIZONTAL: cover a pantalla completa (comportamiento actual). */
          <VideoTrack trackRef={remoteVideo} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: '#0c1413' }} />
        )
      ) : (
        /* Placeholder del paciente: distingue DESCONECTADO (salió del room →
           sin ningún track) de CÁMARA APAGADA (presente, solo audio). */
        <div className={`${styles.avatarPlaceholder} ${styles.audioFirstStage}`}>
          <div className={styles.audioFirstWrap}>
            {/* Los anillos animados sugieren actividad: cuando el paciente está
                presente (audio en vivo) o mientras se le está llamando. Solo el
                "desconectado" (ya entró y salió) queda con círculo estático. */}
            <div className={(pacientePresente || !pacienteYaEntro) ? styles.audioRings : undefined}>
              {/* Fallback 'P': si el nombre llega vacío al inicio, el círculo
                  no debe quedar sin contenido. */}
              <Avatar className={styles.avatarCircleLg} nombre={patientName || 'Paciente'} fotoURL={patientFotoURL} fallback={getInitials(patientName || 'Paciente') || 'P'} zoomable />
            </div>
            <p className={styles.audioFirstName}>{patientName || 'Paciente'}</p>
            {pacientePresente ? (
              <>
                {/* No afirmar "apagada" cuando en realidad no llegó el medio:
                    para el médico son situaciones distintas —una la decidió el
                    paciente y la otra hay que resolverla—. */}
                {camSinSenal ? (
                  <span className={`${styles.audioFirstPill} ${styles.audioFirstPillMuted}`}>
                    <WifiOff size={12} aria-hidden="true" /> Sin señal de video · reconectando
                  </span>
                ) : pacienteEnSoloAudio ? (
                  /* La app del paciente apagó SU cámara para que el poco ancho de banda que
                     tiene le alcance al audio, y lo declara con el atributo `modoSoloAudio`.
                     Sin leerlo, esto se veía igual que «apagada» y el médico entendía que fue
                     una decisión del paciente: la historia equivocada. */
                  <span className={styles.audioFirstPill}>
                    <AudioLines size={12} aria-hidden="true" /> Sin video por su conexión · lo escucha con normalidad
                  </span>
                ) : (
                  <span className={styles.audioFirstPill}>Cámara apagada · el audio continúa</span>
                )}
                {micMuted && (
                  <span className={`${styles.audioFirstPill} ${styles.audioFirstPillMuted}`}>
                    <MicOff size={12} aria-hidden="true" /> Micrófono silenciado
                  </span>
                )}
                {micSinSenal && (
                  <span className={`${styles.audioFirstPill} ${styles.audioFirstPillMuted}`}>
                    <WifiOff size={12} aria-hidden="true" /> Sin señal de audio · reconectando
                  </span>
                )}
              </>
            ) : pacienteYaEntro ? (
              <span className={`${styles.audioFirstPill} ${styles.audioFirstPillMuted}`}>
                <WifiOff size={12} aria-hidden="true" /> El paciente se desconectó
              </span>
            ) : (
              <EstadoAviso aviso={avisoLlamada} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Píldora de estado del pipeline de IA (escuchando/en cola/procesando) ── */
type AiStatus = 'listening' | 'queued' | 'processing' | 'idle';

function AiStatusPill({ status }: { status: AiStatus }) {
  return (
    <span
      className={`${styles.aiStatusPill} ${styles[`aiStatus_${status}`]}`}
      title={
        status === 'listening' ? 'Escuchando y transcribiendo audio' :
        status === 'queued' ? 'Transcripción en cola, pendiente de procesar' :
        status === 'processing' ? 'Procesando nota médica con IA' :
        'Esperando audio'
      }
    >
      <span className={styles.aiStatusBars} aria-hidden="true">
        <span /><span /><span /><span />
      </span>
      {status === 'listening' ? 'Escuchando' :
       status === 'queued' ? 'En cola' :
       status === 'processing' ? 'Procesando' :
       'Esperando'}
    </span>
  );
}

/* ── Hilo de conversación de la transcripción (estilo chat tipo Claude) ──
   Un ÚNICO render compartido por la pestaña "Transcripción" y la caption bar
   bajo el video (misma fuente de datos, mismo diseño). */
function TranscriptThread({ transcript, interimText, interimSpeaker, doctorName, patientName }: {
  transcript: TranscriptEntry[];
  interimText: string;
  interimSpeaker: string;
  doctorName: string;
  patientName: string;
}) {
  // Group consecutive entries by speaker
  const groups: { speaker: string; texts: string[]; firstId: number; lastTs: Date }[] = [];
  for (const entry of transcript) {
    const last = groups[groups.length - 1];
    if (last && last.speaker === entry.speaker) {
      last.texts.push(entry.text);
      last.lastTs = entry.timestamp;
    } else {
      groups.push({ speaker: entry.speaker, texts: [entry.text], firstId: entry.id, lastTs: entry.timestamp });
    }
  }
  const interimBelongsToLastGroup = !!interimText && groups.length > 0 && groups[groups.length - 1].speaker === interimSpeaker;
  const fmtTime = (d: Date) => d instanceof Date ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className={styles.txThread}>
      {groups.map((group, gi) => {
        const isDoctor = group.speaker === 'Doctor';
        const showInterimHere = interimBelongsToLastGroup && gi === groups.length - 1;
        return (
          <div key={group.firstId} className={isDoctor ? styles.txRowDoctor : styles.txRowPatient}>
            <span className={`${styles.txSpeaker} ${isDoctor ? styles.txSpeakerDoctor : styles.txSpeakerPatient}`}>
              {isDoctor ? (doctorName || 'Médico') : (patientName || 'Paciente')}
            </span>
            <div className={styles.txTextBlock}>
              <p className={styles.txMsg}>{group.texts.join(' ')}</p>
              {showInterimHere && <p className={styles.txMsgInterim}>{interimText}</p>}
            </div>
            <span className={styles.txTime}>{fmtTime(group.lastTs)}</span>
          </div>
        );
      })}
      {interimText && !interimBelongsToLastGroup && (
        <div className={interimSpeaker === 'Doctor' ? styles.txRowDoctor : styles.txRowPatient}>
          <span className={`${styles.txSpeaker} ${interimSpeaker === 'Doctor' ? styles.txSpeakerDoctor : styles.txSpeakerPatient}`}>
            {interimSpeaker === 'Doctor' ? (doctorName || 'Médico') : (patientName || 'Paciente')}
          </span>
          <div className={styles.txTextBlock}>
            <p className={styles.txMsgInterim}>{interimText}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Indicador para el MÉDICO de que un supervisor está observando la consulta.
 * Detecta participantes remotos con identidad `supervisor_…`, muestra un chip
 * persistente con su nombre y un aviso temporal cuando alguien se une.
 */
function SupervisorIndicator() {
  const participants = useRemoteParticipants();
  const supervisores = participants.filter(
    (p) => (p.identity ?? '').startsWith('supervisor_'),
  );
  const key = supervisores.map((p) => p.identity).join(',');
  const nombres = supervisores.map((p) => p.name || 'Supervisor');

  const [justJoined, setJustJoined] = useState<string | null>(null);
  const prevRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const cur = new Set(supervisores.map((p) => p.identity ?? ''));
    for (const p of supervisores) {
      const id = p.identity ?? '';
      if (id && !prevRef.current.has(id)) setJustJoined(p.name || 'Un supervisor');
    }
    prevRef.current = cur;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!justJoined) return;
    const t = setTimeout(() => setJustJoined(null), 6000);
    return () => clearTimeout(t);
  }, [justJoined]);

  if (supervisores.length === 0) return null;
  return (
    <>
      <div className={styles.supervisorChip}>
        <Eye size={14} aria-hidden="true" /> {nombres.join(', ')} supervisando
      </div>
      {justJoined && (
        <div className={styles.supervisorToast} role="status">
          <Eye size={15} aria-hidden="true" /> {justJoined} se unió como supervisor
        </div>
      )}
    </>
  );
}

/* Solo anuncia la conexión INICIAL. Las interrupciones a mitad de llamada las gobierna la
   escalera de avisos (`AvisosConexion`): silencio los primeros 8 s, banner discreto entre 8 y
   25 s, instrucción después. Antes este banner decía «Reconectando...» al instante, y eso es
   justo lo que la propuesta pide evitar: la mayoría de esas microcaídas se resuelven solas y
   anunciarlas enseña a desconfiar de una llamada que estaba a punto de volver. */
function ConnectionBanner() {
  const state = useConnectionState();
  if (state !== ConnectionState.Connecting) return null;
  return (
    <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', background: 'rgba(12,20,19,0.85)', backdropFilter: 'blur(12px)', color: '#fff', padding: '8px 20px', borderRadius: 12, fontSize: 13, zIndex: 10, fontWeight: 500, border: '1px solid rgba(255,255,255,0.08)' }}>
      Conectando...
    </div>
  );
}

/* ── Watchdog de reconexión: corta el BUCLE DE RESUME del SDK ──
   Incidente real (jul 2026): un médico (Firefox/Windows) quedó atascado en un
   loop cada ~16 s — el cliente intentaba REANUDAR (resume) una sesión que el
   servidor ya había cerrado (SIGNAL_SOURCE_CLOSE, "could not restart
   participant", sessionDuration 0s) y reintentaba indefinidamente en vez de
   hacer una conexión fresca. El clúster y los demás participantes estaban bien.

   Este watchdog observa el ConnectionState dentro de <LiveKitRoom>: si la
   conexión lleva AUTO_REJOIN_AFTER_MS continuos en problemas (reconnecting /
   disconnected) sin lograr conectar, dispara `onForceRejoin` → el padre
   invalida el token, pide uno fresco y REMONTA <LiveKitRoom> (key=
   reconnectNonce), creando un Room y una sesión de participante NUEVOS (join
   fresco, no resume). `onStable` avisa cuando la conexión se mantiene sana
   para resetear el contador de intentos automáticos del padre. */
const AUTO_REJOIN_AFTER_MS = 15_000; // margen para el auto-reconnect propio del SDK
const AUTO_REJOIN_STABLE_MS = 30_000; // conexión sana continua → reset de intentos
// Estancamiento en la conexión INICIAL: cuando el servidor self-hosted
// (rtc-dev.rt.voxlivia.com) se está reiniciando por mantenimiento justo al
// entrar, el cliente queda en `Connecting` para SIEMPRE ("Conectando..." sin
// avanzar) — nunca pasa a `Reconnecting`/`Disconnected`, así que el watchdog de
// resume no lo cubría. Tras este margen (más generoso que el de resume, porque
// un connect inicial legítimo sobre TURN/relay puede tardar unos segundos)
// forzamos un join fresco: token nuevo + remount de <LiveKitRoom>. Si el
// servidor ya volvió, reconecta; si sigue caído, el tope de intentos deja el
// botón manual del overlay.
const AUTO_REJOIN_CONNECTING_MS = 20_000;

function AutoReconnectWatchdog({ onForceRejoin, onStable }: {
  onForceRejoin: () => void;
  onStable: () => void;
}) {
  const state = useConnectionState();
  const inTrouble = state === ConnectionState.Reconnecting || state === ConnectionState.Disconnected;
  // El `Connecting` estancado se trata como problema, pero con su propio
  // umbral (más largo) para no cortar un connect inicial lento legítimo.
  const isConnecting = state === ConnectionState.Connecting;
  // Un solo disparo por racha de problemas: si el rejoin no resuelve, el
  // componente se remonta con el Room nuevo y el ciclo puede repetirse (el
  // padre limita el total de intentos automáticos).
  const firedRef = useRef(false);

  useEffect(() => {
    if (!inTrouble && !isConnecting) {
      firedRef.current = false;
      return;
    }
    if (firedRef.current) return;
    // Umbral según el tipo de problema: resume estancado (reconnecting/
    // disconnected) usa el margen corto; connect inicial estancado usa el largo.
    const delay = inTrouble ? AUTO_REJOIN_AFTER_MS : AUTO_REJOIN_CONNECTING_MS;
    const t = setTimeout(() => {
      firedRef.current = true;
      onForceRejoin();
    }, delay);
    return () => clearTimeout(t);
  }, [inTrouble, isConnecting, onForceRejoin]);

  useEffect(() => {
    if (state !== ConnectionState.Connected) return;
    const t = setTimeout(onStable, AUTO_REJOIN_STABLE_MS);
    return () => clearTimeout(t);
  }, [state, onStable]);

  return null;
}


function DoctorLocalVideo({ doctorName, doctorFotoURL, cameraOn, cameraError }: {
  doctorName: string;
  doctorFotoURL?: string | null;
  cameraOn: boolean;
  cameraError: boolean;
}) {
  const { localParticipant } = useLocalParticipant();
  const cameraPub = localParticipant?.getTrackPublication(Track.Source.Camera);
  const trackRef = useMemo(() => {
    if (!cameraPub || !cameraPub.track) return null;
    return {
      participant: localParticipant,
      source: Track.Source.Camera,
      publication: cameraPub,
    };
  }, [cameraPub, localParticipant]);

  if (cameraOn && !cameraError && trackRef && cameraPub?.track) {
    return (
      <>
        <VideoTrack
          trackRef={trackRef as Parameters<typeof VideoTrack>[0]['trackRef']}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
        />
      </>
    );
  }

  return (
    <div className={styles.avatarPlaceholder}>
      <Avatar className={styles.avatarCircleLg} nombre={doctorName} fotoURL={doctorFotoURL} />
    </div>
  );
}

/* Aplica el MUTE real del micrófono y la cámara del médico en el room (los
   props audio=/video= de LiveKitRoom no toggglean de forma fiable en runtime →
   el paciente seguía escuchando/viendo aunque el médico apagara mic o cámara).
   Debe vivir DENTRO de <LiveKitRoom> para tener el RoomContext. */
/* Cada cuánto se comprueba que lo que el médico cree tener encendido esté de
   verdad publicado, y cuántas veces se reintenta antes de rendirse y pedir un
   join fresco. */
const REPUBLICAR_CADA_MS = 4_000;
const REPUBLICAR_MAX_INTENTOS = 3;
/* Margen tras conectar (o tras encender/apagar un medio) antes de dar algo por
   «faltante». Publicar tarda unos segundos —abrir el dispositivo, negociar la
   pista—, y sin margen el bucle empezaba a «recuperar» lo que aún se estaba
   publicando y a los 16 s forzaba un join fresco que cortaba al paciente
   (26-oct-2026, prod). */
const REPUBLICAR_MARGEN_MS = 8_000;

/* Errores PASAJEROS al publicar: el engine aún está conectando o reconectando.
   Se resuelven solos al estabilizarse la conexión; no son una falla. */
const ES_PUBLICACION_PASAJERA = /engine not connected|publishing rejected|not connected within timeout/i;

/* ── Fallas del DISPOSITIVO, no de la conexión ──
   Si el equipo no tiene micrófono (o el navegador no deja usarlo), reintentar
   cada 4 s y forzar un join fresco no lo arregla: corta la llamada una y otra
   vez. Pasó en prod el 26-oct-2026: un médico sin micrófono generó 57 errores
   en 15 min, reconexiones cada 15-30 s que terminaron desconectando al
   paciente, y una alerta FATAL de «backend caído» — mientras él no sabía que
   el paciente no lo escuchaba. Estas fallas se detectan aparte, se le avisan
   al médico y se dejan de reintentar hasta que algo cambie. */
type FallaDispositivo = 'no_encontrado' | 'sin_permiso' | 'ocupado';
type FuenteLocal = 'mic' | 'cam';

function fallaDeDispositivo(e: unknown): FallaDispositivo | null {
  const t = `${(e as { name?: string })?.name ?? ''} ${String(e)}`;
  if (/NotFoundError|DevicesNotFoundError|OverconstrainedError/.test(t)) return 'no_encontrado';
  if (/NotAllowedError|PermissionDeniedError/.test(t)) return 'sin_permiso';
  if (/NotReadableError|TrackStartError/.test(t)) return 'ocupado';
  return null;
}

const TEXTO_FALLA: Record<FuenteLocal, Record<FallaDispositivo, string>> = {
  mic: {
    no_encontrado: 'No se detecta su micrófono: el paciente no lo escucha. Conecte uno; la llamada lo tomará al detectarlo.',
    sin_permiso: 'El navegador no permite usar su micrófono: el paciente no lo escucha. Habilítelo en el candado de la barra de direcciones.',
    ocupado: 'Otro programa está usando su micrófono: el paciente no lo escucha. Ciérrelo y pulse Reintentar.',
  },
  cam: {
    no_encontrado: 'No se detecta su cámara: el paciente no lo ve. Conecte una; la llamada la tomará al detectarla.',
    sin_permiso: 'El navegador no permite usar su cámara: el paciente no lo ve. Habilítela en el candado de la barra de direcciones.',
    ocupado: 'Otro programa está usando su cámara: el paciente no lo ve. Ciérrelo y pulse Reintentar.',
  },
};

function MediaSync({ micEnabled, camEnabled, consultaId, reconnectNonce, onPublicacionPerdida, onCamaraSinPublicar }: {
  micEnabled: boolean;
  camEnabled: boolean;
  consultaId?: string;
  /** Correlación con LLAMADA_REINTENTO / LLAMADA_REJOIN_AUTO / LLAMADA_IDENTIDAD_DUPLICADA. */
  reconnectNonce?: number;
  onPublicacionPerdida?: () => void;
  /** La cámara está encendida pero el servidor no tiene su pista. */
  onCamaraSinPublicar?: (falla: boolean) => void;
}) {
  const { localParticipant } = useLocalParticipant();

  // Fuentes cuyo dispositivo falló (ver fallaDeDispositivo). El ref lo lee el
  // intervalo de reconciliación; el estado pinta el aviso.
  const sinDispositivoRef = useRef<Partial<Record<FuenteLocal, FallaDispositivo>>>({});
  const [sinDispositivo, setSinDispositivo] = useState<Partial<Record<FuenteLocal, FallaDispositivo>>>({});
  const olvidarFalla = useCallback((fuente?: FuenteLocal) => {
    if (fuente) delete sinDispositivoRef.current[fuente];
    else sinDispositivoRef.current = {};
    setSinDispositivo({ ...sinDispositivoRef.current });
  }, []);
  // Se vuelve a intentar cuando cambia algo que puede arreglarlo: conectaron o
  // quitaron un dispositivo, o el médico apaga y enciende ese medio.
  useEffect(() => {
    const md = navigator.mediaDevices;
    if (!md?.addEventListener) return;
    const alCambiar = () => olvidarFalla();
    md.addEventListener('devicechange', alCambiar);
    return () => md.removeEventListener('devicechange', alCambiar);
  }, [olvidarFalla]);
  useEffect(() => { olvidarFalla('mic'); }, [micEnabled, olvidarFalla]);
  useEffect(() => { olvidarFalla('cam'); }, [camEnabled, olvidarFalla]);

  useEffect(() => {
    if (!localParticipant) return;
    localParticipant
      .setMicrophoneEnabled(micEnabled)
      .catch((e) => console.warn('[VideoCall] setMicrophoneEnabled:', e));
  }, [micEnabled, localParticipant]);
  useEffect(() => {
    if (!localParticipant) return;
    localParticipant
      .setCameraEnabled(camEnabled)
      .catch((e) => console.warn('[VideoCall] setCameraEnabled:', e));
  }, [camEnabled, localParticipant]);

  /* ── Reconciliar lo que se CREE publicado con lo que el servidor tiene ──
     Los dos efectos de arriba solo corren cuando CAMBIA el interruptor. En una
     reconexión por «resume» el participante local es el mismo objeto y los
     interruptores no se movieron, así que no vuelven a correr — pero el
     servidor sí puede haber dado de baja las pistas al ver caer el transporte.
     Ahí el médico queda dentro de la sala publicando NADA: para el paciente es
     indistinguible de haber apagado cámara y micrófono, y así se lo dice su
     app. Pasó el 25-ago-2026 a las 09:26: el cliente se reconectó en 5 s y
     estuvo 95 s sin republicar, hasta que una segunda caída forzó un join
     completo. Nadie lo detectaba porque el estado de conexión decía
     «Connected» y era cierto.

     Esto compara intención contra realidad y republica lo que falte. Volver a
     llamar a setXEnabled(true) sobre algo ya publicado no hace nada, así que
     es seguro repetirlo. Si tras varios intentos sigue sin publicar, el
     problema no es la publicación sino la sesión: se pide un join fresco. */
  const estadoConexion = useConnectionState();
  const intentosRef = useRef(0);

  /* ── Qué entrega REALMENTE la cámara ──
     La reconciliación de abajo comprueba que la publicación exista, pero una
     pista publicada puede no llevar un solo frame: para el servidor y para el
     cliente todo parece correcto y el video no aparece en ninguna pantalla. Es
     lo que se vio el 27 y el 28 de agosto, y no había forma de distinguirlo de
     un problema de red o de la cámara apagada.
     Esto lee el estado del dispositivo —vivo o terminado, silenciado, y qué
     resolución está entregando— una sola vez, cuando ya debería estar en
     marcha. Es la diferencia entre «la cámara no produce» y «produce pero no
     llega». */
  const informeRef = useRef(false);
  useEffect(() => {
    if (!localParticipant || estadoConexion !== ConnectionState.Connected) return;
    if (informeRef.current || !camEnabled) return;
    const t = setTimeout(() => {
      if (informeRef.current) return;
      informeRef.current = true;
      const pub = localParticipant.getTrackPublication(Track.Source.Camera);
      const mst = (pub?.track as { mediaStreamTrack?: MediaStreamTrack } | undefined)?.mediaStreamTrack;
      const cfg = mst?.getSettings?.() ?? {};
      logClient({
        nivel: mst && mst.readyState === 'live' && !mst.muted ? 'INFO' : 'WARN',
        categoria: 'LLAMADA', codigo: 'CAMARA_ESTADO_REAL',
        componente: 'VideoCallPageV2', consultaId,
        resultado: mst?.readyState === 'live' ? 'ok' : 'rechazado',
        mensaje: !pub ? 'La cámara no está publicada'
          : !mst ? 'Publicada pero sin pista de dispositivo'
          : mst.readyState === 'ended' ? 'PUBLICADA PERO EL DISPOSITIVO SE SOLTÓ (ended): no llega imagen'
          : `Cámara ${mst.readyState}${mst.muted ? ', SILENCIADA por el navegador' : ''}`
            + ` · ${cfg.width ?? '?'}x${cfg.height ?? '?'} · ${String(mst.label || 'sin nombre').slice(0, 60)}`,
      });
    }, 10_000);
    return () => clearTimeout(t);
  }, [localParticipant, estadoConexion, camEnabled, consultaId]);

  /* ── Tras cada (re)conexión: afirmar lo que queremos publicar y dejar constancia ──
     El 04-09-2026 el servidor expulsó al médico por identidad duplicada, la sesión que quedó
     viva volvió a entrar a la sala y NO publicó nada —ni audio ni video— durante tres minutos.
     Nadie lo detectó: para esa pestaña la conexión estaba «bien».

     Dos medidas. Primero, no confiar en los parámetros del momento de conectar: al volver a
     estar conectados se REAFIRMA la intención del médico (cámara y micrófono según lo que
     tiene encendido). Segundo, registrar a los pocos segundos qué quedó realmente publicado,
     que es lo que faltaba para diagnosticar sin leer los webhooks de LiveKit. */
  const generacionRef = useRef(0);
  const estabaConectadoRef = useRef(false);
  useEffect(() => {
    if (!localParticipant) return;
    if (estadoConexion !== ConnectionState.Connected) {
      estabaConectadoRef.current = false;
      return;
    }
    const esReconexion = !estabaConectadoRef.current && generacionRef.current > 0;
    estabaConectadoRef.current = true;
    generacionRef.current += 1;
    const generacion = generacionRef.current;

    const reafirmar = async () => {
      if (!esReconexion) return;
      try {
        await localParticipant.setMicrophoneEnabled(micEnabled);
        await localParticipant.setCameraEnabled(camEnabled);
      } catch (err) {
        logClient({
          nivel: 'WARN', categoria: 'LLAMADA', codigo: 'REPUBLICAR_TRAS_RECONEXION_FALLO',
          componente: 'VideoCallPageV2', consultaId, resultado: 'error',
          mensaje: `No se pudo reafirmar la publicación tras reconectar: ${err instanceof Error ? err.message : String(err)}`,
        });
      }
    };
    void reafirmar();

    const t = setTimeout(() => {
      if (generacion !== generacionRef.current) return;
      const cam = localParticipant.getTrackPublication(Track.Source.Camera);
      const mic = localParticipant.getTrackPublication(Track.Source.Microphone);
      const faltaTodo = !cam && !mic;
      logClient({
        nivel: faltaTodo ? 'ERROR' : (camEnabled && !cam) || (micEnabled && !mic) ? 'WARN' : 'INFO',
        categoria: 'LLAMADA', codigo: 'PUBLICACION_PROPIA',
        componente: 'VideoCallPageV2', consultaId,
        resultado: faltaTodo ? 'error' : 'ok',
        mensaje: faltaTodo
          ? 'NO estamos publicando nada: el paciente no recibe ni audio ni video'
          : `Publicando cámara=${cam ? (cam.isMuted ? 'silenciada' : 'sí') : 'NO'}`
            + ` · micrófono=${mic ? (mic.isMuted ? 'silenciado' : 'sí') : 'NO'}`
            + (esReconexion ? ' (tras reconectar)' : ''),
        detalle: {
          reconexion: esReconexion,
          // Correlación con LLAMADA_REINTENTO / LLAMADA_REJOIN_AUTO / LLAMADA_IDENTIDAD_DUPLICADA:
          // si esto sube junto con una racha de expulsiones, la causamos nosotros remontando
          // demasiado rápido; si las expulsiones llegan sin que esto se mueva, es una sesión
          // ajena (otro dispositivo del médico) y el candado de esta pestaña no la ve.
          intentoConexion: reconnectNonce,
          quiereCamara: camEnabled, camaraPublicada: !!cam, camaraMuteada: cam?.isMuted ?? null,
          quiereMicrofono: micEnabled, microfonoPublicado: !!mic, microfonoMuteado: mic?.isMuted ?? null,
        },
      });
    }, 5_000);
    return () => clearTimeout(t);
  }, [localParticipant, estadoConexion, camEnabled, micEnabled, consultaId]);

  useEffect(() => {
    if (!localParticipant || estadoConexion !== ConnectionState.Connected) {
      intentosRef.current = 0;
      return;
    }
    /* Qué cuenta como «falta».
       No basta con que exista la publicación: tras perder la red, el navegador
       puede SOLTAR la cámara y dejar la pista publicada con un dispositivo ya
       muerto (`readyState === 'ended'`). Para el servidor y para el SDK todo
       parece correcto —hay publicación, hay track— y no llega un solo frame.
       Eso es justo lo que se reporta: al reconectar, audio bien y video del
       médico ausente en las dos pantallas, sin que nada se queje. */
    const falta = (fuente: Track.Source, seQuiere: boolean) => {
      if (!seQuiere) return false;
      // Sin dispositivo no hay nada que republicar: no cuenta como «falta» y
      // por eso tampoco dispara el join fresco.
      if (sinDispositivoRef.current[fuente === Track.Source.Camera ? 'cam' : 'mic']) return false;
      const pub = localParticipant.getTrackPublication(fuente);
      if (!pub || !pub.track) return true;
      const mst = (pub.track as { mediaStreamTrack?: MediaStreamTrack }).mediaStreamTrack;
      return !mst || mst.readyState === 'ended';
    };

    /* Recuperar de verdad, no solo pedirlo.
       `setCameraEnabled(true)` NO reabre la cámara: si ya hay publicación solo
       hace `unmute()` —inútil con el dispositivo muerto— y si no la hay puede
       quedarse esperando una publicación anterior a medias que la desconexión
       dejó colgada, devolviendo sin publicar y sin lanzar error. Eso explica
       los `LLAMADA_REPUBLICA` del lunes sin una sola pista nueva en el
       servidor. Aquí se ataca cada caso con lo que de verdad lo arregla. */
    const recuperar = async (fuente: Track.Source): Promise<string> => {
      const pub = localParticipant.getTrackPublication(fuente);
      const pista = pub?.track as
        { mediaStreamTrack?: MediaStreamTrack; restartTrack?: () => Promise<void> } | undefined;

      // (a) La publicación está, pero el dispositivo se soltó: reabrirlo.
      if (pista?.restartTrack && (!pista.mediaStreamTrack || pista.mediaStreamTrack.readyState === 'ended')) {
        await pista.restartTrack();
        return 'reinicio de pista';
      }
      // (b) No hay publicación: apagar y encender limpia el estado a medias
      //     del SDK antes de volver a publicar.
      const esCam = fuente === Track.Source.Camera;
      const set = (v: boolean) => esCam
        ? localParticipant.setCameraEnabled(v)
        : localParticipant.setMicrophoneEnabled(v);
      await set(false);
      await new Promise((r) => setTimeout(r, 300));
      await set(true);
      return 'apagar y encender';
    };
    const conectadoDesde = Date.now();
    let soloCamaraAvisado = false;
    const enCurso = new Set<Track.Source>();
    const t = setInterval(() => {
      if (Date.now() - conectadoDesde < REPUBLICAR_MARGEN_MS) return;
      const faltaCam = falta(Track.Source.Camera, camEnabled);
      const faltaMic = falta(Track.Source.Microphone, micEnabled);
      // Fuente única del estado de la cámara: lo que el servidor tiene
      // publicado, no un sondeo aparte que además la estropeaba.
      onCamaraSinPublicar?.(faltaCam);
      if (!faltaCam && !faltaMic) { intentosRef.current = 0; soloCamaraAvisado = false; return; }

      intentosRef.current += 1;
      // Si el audio llega, la consulta sigue: un join fresco sacaría a los dos
      // de la sala solo por la cámara. Se sigue reintentándola sin reconectar.
      const audioFluye = micEnabled && !faltaMic;
      if (intentosRef.current > REPUBLICAR_MAX_INTENTOS && audioFluye) {
        intentosRef.current = 0;
        if (!soloCamaraAvisado) {
          soloCamaraAvisado = true;
          logClient({
            nivel: 'WARN', categoria: 'LLAMADA', codigo: 'LLAMADA_SIN_PUBLICAR',
            componente: 'VideoCallPageV2', consultaId, resultado: 'reintento',
            mensaje: `Sin cámara publicada tras ${REPUBLICAR_MAX_INTENTOS} intentos con el audio funcionando; `
              + 'se sigue reintentando la cámara sin reconectar',
          });
        }
      } else if (intentosRef.current > REPUBLICAR_MAX_INTENTOS) {
        logClient({
          nivel: 'WARN', categoria: 'LLAMADA', codigo: 'LLAMADA_SIN_PUBLICAR',
          componente: 'VideoCallPageV2', consultaId, resultado: 'reintento',
          mensaje: `Conectado pero sin publicar tras ${REPUBLICAR_MAX_INTENTOS} intentos `
            + `(cámara=${faltaCam ? 'falta' : 'ok'}, micrófono=${faltaMic ? 'falta' : 'ok'}); se fuerza un join fresco`,
        });
        intentosRef.current = 0;
        onPublicacionPerdida?.();
        return;
      }

      logClient({
        nivel: 'WARN', categoria: 'LLAMADA', codigo: 'LLAMADA_REPUBLICA',
        componente: 'VideoCallPageV2', consultaId, resultado: 'reintento',
        mensaje: `Conectado pero el servidor no tiene la publicación `
          + `(cámara=${faltaCam ? 'falta' : 'ok'}, micrófono=${faltaMic ? 'falta' : 'ok'}); republicando #${intentosRef.current}`,
      });
      /* El resultado se REGISTRA. Antes un fallo aquí iba a console.warn y no
         salía de la máquina del médico: se veía el intento de republicar y
         nunca si había funcionado, que es la mitad que importa. */
      const intentar = (fuente: Track.Source, etiqueta: string) => {
        // Uno a la vez por fuente: con el engine reconectando un intento puede
        // tardar más que el intervalo, y se apilaban (seis resueltos de golpe
        // el 26-oct) compitiendo por el mismo dispositivo.
        if (enCurso.has(fuente)) return;
        enCurso.add(fuente);
        recuperar(fuente).finally(() => enCurso.delete(fuente)).then(
          (via) => logClient({
            nivel: 'INFO', categoria: 'LLAMADA', codigo: 'LLAMADA_REPUBLICA_OK',
            componente: 'VideoCallPageV2', consultaId, resultado: 'ok',
            mensaje: `${etiqueta} recuperado por ${via}`,
          }),
          (e) => {
            const falla = fallaDeDispositivo(e);
            if (!falla) {
              // Con el engine aún conectando es pasajero: WARN, no cuenta para
              // la salud del backend; el siguiente ciclo lo vuelve a intentar.
              const pasajero = ES_PUBLICACION_PASAJERA.test(String(e));
              logClient({
                nivel: pasajero ? 'WARN' : 'ERROR', categoria: 'LLAMADA', codigo: 'LLAMADA_REPUBLICA_FALLO',
                componente: 'VideoCallPageV2', consultaId, resultado: pasajero ? 'reintento' : 'error',
                mensaje: `No se pudo recuperar ${etiqueta}: ${String(e).slice(0, 160)}`,
              });
              return;
            }
            // Una sola vez por falla: es del equipo del médico, no del sistema
            // (WARN, así no cuenta para la salud del backend).
            const clave: FuenteLocal = fuente === Track.Source.Camera ? 'cam' : 'mic';
            if (sinDispositivoRef.current[clave] === falla) return;
            sinDispositivoRef.current[clave] = falla;
            setSinDispositivo({ ...sinDispositivoRef.current });
            logClient({
              nivel: 'WARN', categoria: 'LLAMADA', codigo: 'LLAMADA_SIN_DISPOSITIVO',
              componente: 'VideoCallPageV2', consultaId, resultado: 'rechazado',
              mensaje: `Sin ${etiqueta} utilizable (${falla}); se avisa al médico y se deja de reintentar: ${String(e).slice(0, 120)}`,
            });
          },
        );
      };
      if (faltaMic) intentar(Track.Source.Microphone, 'el micrófono');
      if (faltaCam) intentar(Track.Source.Camera, 'la cámara');
    }, REPUBLICAR_CADA_MS);
    return () => clearInterval(t);
  }, [localParticipant, estadoConexion, micEnabled, camEnabled, consultaId,
      onPublicacionPerdida, onCamaraSinPublicar]);

  /* RF-5: cancelación de ruido (Krisp) ACTIVA por defecto sobre el micrófono del
     médico. Se auto-enciende una sola vez por sesión en cuanto el audioTrack del
     micrófono está disponible, sin volver a forzarla si el usuario la apaga. */
  const micAudioTrack =
    (localParticipant?.getTrackPublication(Track.Source.Microphone)?.audioTrack as
      | LocalAudioTrack
      | undefined) ?? null;
  const { enabled, enable, supported } = useNoiseCancellation(micAudioTrack);
  const autoEnabledRef = useRef(false);

  useEffect(() => {
    if (!micAudioTrack) return;
    if (autoEnabledRef.current) return; // ya se auto-activó (o intentó) esta sesión
    if (!supported) return; // navegador sin soporte
    if (enabled) return; // ya está activa
    autoEnabledRef.current = true;
    void enable();
    // No dependemos de `enabled` para no re-forzar tras una desactivación manual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micAudioTrack, supported, enable]);

  const avisos = (['mic', 'cam'] as const)
    .filter((f) => sinDispositivo[f])
    .map((f) => TEXTO_FALLA[f][sinDispositivo[f]!]);
  if (avisos.length === 0) return null;
  return (
    <div role="alert" style={{ position: 'absolute', top: 52, left: '50%', transform: 'translateX(-50%)', zIndex: 15, maxWidth: 'min(560px, calc(100% - 32px))', background: 'rgba(127,29,29,0.92)', backdropFilter: 'blur(12px)', color: '#fff', padding: '10px 14px', borderRadius: 12, fontSize: 13, lineHeight: 1.4, border: '1px solid rgba(255,255,255,0.12)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <AlertTriangle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        {avisos.map((t) => <div key={t}>{t}</div>)}
      </div>
      <button type="button" onClick={() => olvidarFalla()}
        style={{ flexShrink: 0, background: 'rgba(255,255,255,0.16)', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        Reintentar
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════ */

export default function VideoCallPageV2() {
  const { id: consultaId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Modo revisión: se entra desde el historial a editar la nota; la videollamada
  // queda deshabilitada y se muestra "Guardar cambios y volver al inicio".
  const modoRevision = new URLSearchParams(location.search).get('revision') === '1';
  const { user, role } = useAuth();
  const { addToast } = useToast();
  const canceladaRedirRef = useRef(false);
  const esMedico = role === 'medico';
  const { data: firestoreConsulta } = useDocument<Consulta>('consultas', consultaId);

  // Semilla de la nota persistida (notas_medicas/final) para el editor en modo revisión.
  const [revisionSeed, setRevisionSeed] = useState<{ s: string; o: string; a: string; p: string; e: string } | null>(null);
  const [guardandoRevision, setGuardandoRevision] = useState(false);
  useEffect(() => {
    if (!modoRevision || !consultaId) return;
    const unsub = onSnapshot(doc(db, `consultas/${consultaId}/notas_medicas/final`), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setRevisionSeed({ s: d.s || '', o: d.o || '', a: d.a || '', p: d.p || '', e: d.e || '' });
      } else {
        setRevisionSeed(null); // sin nota final → cae al SOAP en vivo (ia_soap)
      }
    });
    return () => unsub();
  }, [modoRevision, consultaId]);

  const patient = firestoreConsulta
    ? { nombre: firestoreConsulta.pacienteNombre, cedula: firestoreConsulta.pacienteId, salaId: firestoreConsulta.salaId, salaNombre: firestoreConsulta.salaNombre, motivoConsulta: firestoreConsulta.motivoConsulta }
    : EMPTY_PATIENT;

  // Fotos de perfil (denormalizadas en la consulta) para los avatares.
  const patientFotoURL = firestoreConsulta?.pacienteFotoURL ?? null;
  // La foto del médico se toma de la consulta (medicoFotoURL = medicos/{uid}.avatarUrl,
  // la que sube en su perfil), no de user.photoURL (Firebase Auth), que casi nunca
  // está seteada → antes caía a iniciales aunque el médico tuviera foto.
  const doctorFotoURL = firestoreConsulta?.medicoFotoURL ?? user?.photoURL ?? null;

  // Fetch full patient demographics (sexo, edad, alergias, etc.) from pacientes collection
  const [pacienteData, setPacienteData] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    const pacienteId = firestoreConsulta?.pacienteId;
    if (!pacienteId) return;
    const pacienteUid = firestoreConsulta?.pacienteUid;
    const obtenerPacienteFn = httpsCallable(functions, 'portal_obtenerPaciente');
    obtenerPacienteFn({ pacienteId, pacienteUid }).then((res) => {
      const data = res.data as { paciente?: Record<string, unknown> };
      if (data?.paciente) setPacienteData(data.paciente);
    }).catch((err) => {
      console.warn('[VideoCall] No se pudo cargar datos del paciente:', err?.message);
    });
  }, [firestoreConsulta?.pacienteId, firestoreConsulta?.pacienteUid]);

  // M4: datos demográficos desde el API de SEIS/HCIS (FHIR proxy) — sexo/edad
  // que no siempre vienen en el registro del portal ni en la consulta.
  const [fhirPaciente, setFhirPaciente] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    const pacienteId = firestoreConsulta?.pacienteId;
    if (!pacienteId) return;
    const fn = httpsCallable(functions, 'portal_obtenerDatosPaciente');
    fn({ pacienteId, pacienteUid: firestoreConsulta?.pacienteUid, consultaId }).then((res) => {
      const data = res.data as { paciente?: Record<string, unknown> | null };
      if (data?.paciente) setFhirPaciente(data.paciente);
    }).catch((err) => {
      console.warn('[VideoCall] No se pudo cargar datos FHIR del paciente:', err?.message);
    });
  }, [firestoreConsulta?.pacienteId, consultaId]);

  // M4: datos demográficos desde el gateway MINSA (api/paciente/{cedula}),
  // el MISMO endpoint que usa la app móvil. Devuelve caracterSexo/fechaNacimiento.
  // Requiere reenviar el ID token de Firebase del médico (mismo proyecto).
  const [pdpPaciente, setPdpPaciente] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    const pacienteId = firestoreConsulta?.pacienteId;
    if (!pacienteId) return;
    let cancelled = false;
    (async () => {
      try {
        const idToken = await auth.currentUser?.getIdToken();
        const fn = httpsCallable(functions, 'portal_obtenerPacientePdp');
        const res = await fn({ pacienteId, pacienteUid: firestoreConsulta?.pacienteUid, idToken, consultaId });
        const data = res.data as { paciente?: Record<string, unknown> | null };
        if (!cancelled && data?.paciente) setPdpPaciente(data.paciente);
      } catch (err) {
        console.warn('[VideoCall] No se pudo cargar paciente (gateway MINSA):', (err as Error)?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [firestoreConsulta?.pacienteId, consultaId]);

  // Cédula/identificador REAL a mostrar. `pacienteId` suele ser el Firebase UID
  // del paciente (no su cédula) → mostrarlo daba un "consecutivo erróneo" (#7).
  // Preferir la cédula resuelta por el backend; solo caer a pacienteId si de
  // verdad parece una cédula/pasaporte.
  const cedulaPaciente = String(
    (pacienteData?.cedula as string) ||
    (fhirPaciente?.cedula as string) ||
    (pdpPaciente?.cedula as string) ||
    (pareceCedulaStr(firestoreConsulta?.pacienteId) ? firestoreConsulta?.pacienteId : '') ||
    '',
  );

  // M11: ¿el paciente tuvo una teleconsulta finalizada en las últimas 48h?
  // Se consulta su historial reciente; si alguna consulta (distinta a la actual)
  // se finalizó dentro de la ventana, se alerta al médico.
  const [reconsulta48h, setReconsulta48h] = useState(false);
  useEffect(() => {
    const pacienteId = firestoreConsulta?.pacienteId;
    if (!pacienteId) return;
    const fn = httpsCallable(functions, 'portal_obtenerHistorialConsultas');
    fn({ pacienteId, limit: 5 }).then((res) => {
      const data = res.data as { consultas?: Array<{ id: string; finalizadaEn?: unknown }> };
      const lista = data?.consultas ?? [];
      const ahora = Date.now();
      const VENTANA_MS = 48 * 60 * 60 * 1000;
      const tieneReciente = lista.some((c) => {
        if (c.id === consultaId) return false; // excluir la consulta actual
        const ms = tsToMillis(c.finalizadaEn);
        return ms > 0 && ahora - ms <= VENTANA_MS;
      });
      setReconsulta48h(tieneReciente);
    }).catch((err) => {
      console.warn('[VideoCall] No se pudo verificar reconsulta 48h:', err?.message);
    });
  }, [firestoreConsulta?.pacienteId, consultaId]);

  const patientContext = useMemo(
    () => buildPatientContext(firestoreConsulta, pacienteData, fhirPaciente, pdpPaciente),
    [firestoreConsulta?.pacienteNombre, firestoreConsulta?.pacienteEdad, firestoreConsulta?.pacienteSexo, pacienteData, fhirPaciente, pdpPaciente],
  );
  const doctorName = user?.displayName || 'Dr. Juan Pérez';

  /* State */
  const [livekitToken, setLivekitToken] = useState<string | null>(null);
  const [livekitUrl, setLivekitUrl] = useState<string | null>(null);
  const [livekitError, setLivekitError] = useState<string | null>(null);
  /** Motivo conocido de que la conexión no avance (viene del backend). */
  const [avisoConexion, setAvisoConexion] = useState<string | null>(null);
  /** Política de ICE que dictó el servidor para esta sala. */
  const [icePolicy, setIcePolicy] = useState<RTCIceTransportPolicy>('all');
  // Memoizado: un objeto nuevo en cada render haría que LiveKitRoom reconstruya
  // la Room y tumbe la llamada en curso.
  const opcsConexion = useMemo(() => opcionesConexion(icePolicy), [icePolicy]);
  // Se incrementa al "Reintentar conexión": fuerza re-fetch de token fresco y
  // un remount limpio de <LiveKitRoom> sin salir de la pantalla de atención.
  const [reconnectNonce, setReconnectNonce] = useState(0);
  /* Instante del último remount forzado (manual o automático) y un id por «generación» de
     <LiveKitRoom>, para dos cosas que faltaban:
       1) un CANDADO compartido: nada volvía a montar <LiveKitRoom> antes de que la anterior
          terminara de desconectarse. El botón manual y el watchdog automático son
          independientes entre sí, y el 05-sep-2026 a las 14:16 dispararon casi a la vez —el
          médico pulsando «Reintentar» mientras el watchdog ya había forzado un join— y eso
          encadenó CUATRO expulsiones por DUPLICATE_IDENTITY en 25 segundos: cada remount volvía
          a conectar con la misma identidad mientras el anterior aún podía seguir vivo en el
          servidor.
       2) una forma de saber, la próxima vez, si una racha de DUPLICATE_IDENTITY la causamos
          nosotros mismos (se ve un nonce local subiendo justo antes de cada expulsión) o es de
          verdad una segunda sesión ajena (la expulsión llega sin que nuestro nonce se haya
          movido). */
  const ultimoRemontajeMsRef = useRef(0);
  const ESPERA_MIN_ENTRE_REMONTAJES_MS = 5_000;
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [showEndCallModal, setShowEndCallModal] = useState(false);
  // Disposición preseleccionada en el CloseModal (el banner "no contestó /
  // rechazó" la fija; las demás aperturas usan 'completada' por defecto).
  const [disposicionInicialCierre, setDisposicionInicialCierre] =
    useState<DisposicionConsulta | undefined>(undefined);
  const [isEndingCall, setIsEndingCall] = useState(false);
  // Corte local inmediato: al colgar, desmonta <LiveKitRoom> al instante (sin
  // esperar el snapshot de Firestore) para que el cliente se desconecte de forma
  // intencional y NO entre en el bucle de auto-reconexión cuando el servidor
  // cierra la sala.
  const [videoCorteLocal, setVideoCorteLocal] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  /* Peldaño actual de la escalera de avisos de conexión; lo publica `AvisosConexion` y lo
     consume el overlay para saber cuándo le toca interrumpir. */
  const [nivelAvisoConexion, setNivelAvisoConexion] = useState<NivelAviso>('ninguno');
  const { backgrounds: allBackgrounds } = useAdminBackgrounds();
  const [selectedBackground, setSelectedBackground] = useState<BackgroundOption>(allBackgrounds[0]);
  const [stagedBackground, setStagedBackground] = useState<BackgroundOption | null>(null);
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [bgLoading, setBgLoading] = useState(false);

  // ── Persistencia del fondo virtual (medicos/{uid}.fondoVirtual) ────────
  // El último fondo APLICADO queda guardado en el perfil del médico y se
  // restaura en cada videollamada (sin importar el día) hasta que lo cambie
  // o lo quite.
  //
  // Issue #28: fondo virtual POR DEFECTO para todos los médicos. Se distingue:
  //   - Campo AUSENTE (el médico nunca eligió) → se aplica el default
  //     (`blur-light`: sutil, profesional, protege la privacidad del entorno)
  //     y se persiste, de modo que quede recordado.
  //   - `'none'` (el médico lo DESACTIVÓ a propósito) → se respeta, sin fondo.
  //   - un id concreto → se restaura ese fondo.
  const FONDO_POR_DEFECTO = 'blur-light';
  const fondoRestauradoRef = useRef(false);
  useEffect(() => {
    if (fondoRestauradoRef.current || role !== 'medico' || !user?.uid) return;
    if (!allBackgrounds || allBackgrounds.length === 0) return; // catálogo aún cargando
    fondoRestauradoRef.current = true;
    getDoc(doc(db, 'medicos', user.uid))
      .then((snap) => {
        const id = snap.data()?.fondoVirtual as string | undefined;
        // Nunca eligió → aplicar y recordar el default.
        if (id === undefined) {
          const def = allBackgrounds.find((b: BackgroundOption) => b.id === FONDO_POR_DEFECTO);
          if (def) {
            setSelectedBackground(def);
            persistirFondo(def); // recordar para próximas sesiones
          }
          return;
        }
        if (id === 'none') return; // desactivado a propósito → respetar
        const bg = allBackgrounds.find((b: BackgroundOption) => b.id === id);
        if (bg) setSelectedBackground(bg);
      })
      .catch(() => { /* best-effort: sin restauración se queda el default */ });
  }, [role, user?.uid, allBackgrounds]);

  const persistirFondo = useCallback((bg: BackgroundOption) => {
    if (role !== 'medico' || !user?.uid) return;
    updateDoc(doc(db, 'medicos', user.uid), {
      fondoVirtual: bg.id,
      actualizadoEn: serverTimestamp(),
    }).catch(() => { /* best-effort: si falla, solo no queda persistido */ });
  }, [role, user?.uid]);
  const [bgError, setBgError] = useState<string | null>(null);
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(true);

  /** Elegir un fondo lo aplica en el acto (como Teams): el médico ve el
   *  resultado en su propio video sin confirmar nada, y queda recordado. */
  const aplicarFondo = useCallback((bg: BackgroundOption) => {
    setStagedBackground(bg);
    setSelectedBackground(bg);
    persistirFondo(bg);
    setBgError(null);
  }, [persistirFondo]);

  const [isFinalizando, setIsFinalizando] = useState(false);
  const [disposition] = useState<DisposicionConsulta>('completada');
  const [activeTab, setActiveTab] = useState<RightTab>('soap');
  const [soapHasUnread, setSoapHasUnread] = useState(false);
  const [doctorNoteInput, setDoctorNoteInput] = useState('');
  const [videoStarted, setVideoStarted] = useState(false);
  // Atención por teléfono (la videollamada no se pudo establecer). Local para
  // responder al instante; la fuente de verdad es `modalidad` en la consulta,
  // de modo que sobrevive a una recarga.
  const [atencionTelefonicaLocal, setAtencionTelefonica] = useState(false);
  // Cita SEIS: se crea al tomar al paciente. `citaSeisError` alimenta el banner
  // de reintento; si falla el médico igual puede continuar la consulta.
  const [citaSeisError, setCitaSeisError] = useState<string | null>(null);
  const [citaSeisEnviando, setCitaSeisEnviando] = useState(false);
  const citaSeisIntentadaRef = useRef(false);
  // REC-01: consentimiento HIPAA (gate) + indicador de grabación.
  const [showConsent, setShowConsent] = useState(false);
  const [grabacionActiva, setGrabacionActiva] = useState(false);
  const [showExtendMenu, setShowExtendMenu] = useState(false);
  const [extendBusy, setExtendBusy] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [expiryWarningDismissed, setExpiryWarningDismissed] = useState(false);
  const [chatMessages, setChatMessages] = useState<{ role: 'ai' | 'user'; text: string }[]>([
    { role: 'ai', text: 'Estoy escuchando la consulta y redactando la nota SOAPE en tiempo real. Pídeme que modifique la nota (agregar, cambiar o quitar información), prescribir un medicamento, ordenar un examen, un resumen, verificar interacciones o responder cualquier duda clínica sobre el paciente.' },
  ]);
  // El asistente médico (copiloto) está esperando respuesta del modelo.
  const [asistenteBusy, setAsistenteBusy] = useState(false);
  // Sub-tab within the SOAP tab: 'soap' = SOAPE, 'summary' = resumen clínico,
  // 'patient' = resumen para el paciente (visible en la app móvil).
  // Resumen para el paciente (generado por IA, editable, se guarda al finalizar)
  const [resumenPaciente, setResumenPaciente] = useState('');
  const [resumenPacienteLoading, setResumenPacienteLoading] = useState(false);
  const [resumenPacienteError, setResumenPacienteError] = useState<string | null>(null);
  /* Control de frescura del resumen. `resumenFirmaRef` guarda la firma del
   * contenido clínico con el que se generó: si el plan cambia después (la IA
   * agrega un medicamento, el médico pide un examen), el resumen queda
   * DESACTUALIZADO y hay que rehacerlo. Antes se generaba una sola vez y se
   * congelaba — de ahí que llegara al paciente sin sus medicamentos ni sus
   * órdenes. `resumenEditadoRef` protege lo que el médico escribió a mano:
   * eso nunca se pisa automáticamente. */
  const resumenFirmaRef = useRef<string | null>(null);
  const resumenEditadoRef = useRef(false);
  // Resizable panels
  const [sideWidth, setSideWidth] = useState(() => Math.round(window.innerWidth * 2 / 3));
  // Panel del copiloto (a la derecha, redimensionable y ocultable)
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [copilotWidth, setCopilotWidth] = useState(400);
  // Panel izquierdo del video (ocultable). Se oculta con CSS para NO desmontar
  // el <LiveKitRoom> (la llamada sigue activa, solo se oculta el video).
  const [videoOpen, setVideoOpen] = useState(true);
  // Patient video detected aspect ratio (adapts container to source)
  const [patientAspect, setPatientAspect] = useState(16 / 9);
  // Micrófono del PACIENTE silenciado (indicador persistente en el nametag).
  const [patientMicMuted, setPatientMicMuted] = useState(false);
  // Caption bar de transcripción bajo el video: colapsada (~120px) / expandida.
  // El estado se persiste en localStorage entre consultas.
  const [captionOpen, setCaptionOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('videoai-caption-open') === '1'; } catch { return false; }
  });
  const toggleCaption = useCallback(() => {
    setCaptionOpen((v) => {
      const next = !v;
      try { localStorage.setItem('videoai-caption-open', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  /* Refs */
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const transcriptionBodyRef = useRef<HTMLDivElement>(null);
  const captionBodyRef = useRef<HTMLDivElement>(null);

  /* Hooks */
  const { transcript, interimText, interimSpeaker, addSegment, loadSegments, setInterim, setListening: setTranscriptionListening } = useTranscription();

  /* La consulta NO tiene atajos de teclado de una sola letra.
     M (micrófono), V (cámara) y T (transcripción) convivían con la nota SOAPE,
     que se escribe en divs contentEditable. Dos rondas de guards no bastaron
     —issues #36, #44 y #45— y el precio del error lo paga el médico en plena
     consulta: letras que desaparecen mientras redacta la nota clínica.
     Los controles están todos a un clic en la barra inferior; el riesgo no
     compensa. Escape lo maneja cada modal por su cuenta. */

  // Load doctor's background preference on mount.
  useEffect(() => {
    if (!user?.uid || allBackgrounds.length === 0) return;
    getDoctorBgPreference(user.uid).then((savedId) => {
      if (!savedId) return;
      const found = allBackgrounds.find((b) => b.id === savedId);
      if (found) setSelectedBackground(found);
    }).catch(() => { /* ignore */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, allBackgrounds.length]);

  // Save doctor's background preference when changed.
  useEffect(() => {
    if (!user?.uid) return;
    saveDoctorBgPreference(user.uid, selectedBackground.id).catch(() => { /* ignore */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBackground.id]);

  // Consulta CERRADA: la nota está firmada y es inmutable (mismo principio que
  // aplica el backend). Gobierna todo el modo lectura: sin edición, sin guardar
  // y sin ciclos de IA — antes la pantalla dejaba escribir en campos que el
  // servidor iba a rechazar, y el rechazo se mostraba como error de la IA.
  const notaCerrada =
    firestoreConsulta?.estado === 'finalizada' ||
    firestoreConsulta?.estado === 'cancelada';

  // NU: el contexto vivo (NOTA ACTUAL/RECURSOS ACTUALES) ya no se arma en el
  // navegador — el servidor lo lee del documento único notas_medicas/actual.
  const medicalNotes = useMedicalNotes({
    patient: patientContext,
    motivoConsulta: patient.motivoConsulta || '',
    consultaId: consultaId ?? '',
    intervalSeconds: 25,        // ~72 calls máx en 30 min (antes 150 con 12s)
    minCharsToProcess: 80,      // requiere más contenido antes de procesar
    enabled: transcriptionEnabled && !notaCerrada,
  });

  // Only subscribe to Firestore subcollections once the consulta has a medicoId
  // matching the current user (or user is admin/supervisor). This avoids
  // "Missing or insufficient permissions" errors before auto-accept completes.
  const consultaReady =
    !!firestoreConsulta &&
    (firestoreConsulta.medicoId === user?.uid ||
      role === 'admin' ||
      role === 'supervisor');
  const liveSoap = useLiveSoap(consultaReady ? consultaId : undefined);

  const sessionTimer = useSessionDeadline(consultaId);

  // La consulta se resolvió por teléfono: se oculta el panel de iniciar video
  // y se muestra el espacio de nota con «Finalizar atención».
  const atencionTelefonica =
    atencionTelefonicaLocal ||
    (firestoreConsulta as { modalidad?: string } | null)?.modalidad === 'telefonica';

  // Countdown PRE-VIDEO: el médico tomó al paciente y tiene un tiempo limitado
  // para INICIAR la videollamada (preVideoExpiraEn); si no la inicia, el sistema
  // devuelve al paciente a la cola. Solo corre mientras el video no ha iniciado.
  const [preVideoNowTick, setPreVideoNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (videoStarted) return;
    const id = window.setInterval(() => setPreVideoNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [videoStarted]);
  const preVideoExpiraMs =
    (firestoreConsulta?.preVideoExpiraEn as { toMillis?: () => number } | undefined)?.toMillis?.() ?? null;
  const preVideoRemainingSec =
    preVideoExpiraMs != null ? Math.max(0, Math.floor((preVideoExpiraMs - preVideoNowTick) / 1000)) : null;

  // Al AGOTARSE el contador pre-video, el portal devuelve al paciente DE
  // INMEDIATO (misma CF que el botón "Devolver a la cola") en vez de esperar
  // al cron cerrarSesionesExpiradas (corre cada minuto): en esa ventana el
  // médico quedaba ligado a la consulta — el portal le mostraba "tiene una
  // consulta en proceso" y no podía tomar a nadie. El cron sigue siendo el
  // respaldo (pestaña cerrada / sin conexión). Cuando la CF (o el cron, si
  // gana la carrera) re-encola, el watcher de arriba muestra el aviso y
  // navega a /salas.
  const preVideoDevueltoRef = useRef(false);
  useEffect(() => {
    if (!esMedico || videoStarted || modoRevision || preVideoDevueltoRef.current) return;
    if (atencionTelefonica) return; // se atiende por teléfono: no devolver a la cola
    if (preVideoRemainingSec !== 0) return;
    if (firestoreConsulta?.estado !== 'en_atencion' || !consultaId) return;
    preVideoDevueltoRef.current = true;
    const fn = httpsCallable<{ consultaId: string }, { success: boolean }>(functions, 'portal_devolverConsultaACola');
    fn({ consultaId }).catch(() => {
      // failed-precondition = el cron ya la devolvió (o el estado cambió) —
      // el watcher de estado se encarga de navegar en ambos casos.
    });
  }, [esMedico, videoStarted, modoRevision, atencionTelefonica, preVideoRemainingSec, firestoreConsulta?.estado, consultaId]);

  // Video is disabled when session expired, backend finalized, or the doctor
  // ended the call with the patient (M8 first cut: videoFinalizado).
  //
  // Las condiciones del backend (videoFinalizado / estado) NO dependen de
  // `videoStarted` (estado local que se reinicia en cada montaje): si el médico
  // ya colgó y vuelve a entrar a la consulta, debe verse el estado «finalizada»
  // y el botón «Finalizar atención completa», no el panel de iniciar video.
  const videoDisabled =
    atencionTelefonica ||
    firestoreConsulta?.videoFinalizado === true ||
    firestoreConsulta?.estado === 'finalizada' ||
    firestoreConsulta?.estado === 'cancelada' ||
    (videoStarted && (videoCorteLocal || sessionTimer.warningLevel === 'expired'));

  // Issue #8 + timeout pre-video: si mientras el médico está en la pantalla
  // PREVIA (aún sin iniciar el video) la consulta deja de estar disponible, se
  // avisa con un mensaje CLARO y se redirige a la sala. Cubre:
  //   - El paciente canceló/abandonó → estado terminal (finalizada/cancelada).
  //   - El sistema devolvió al paciente a la cola porque el médico no inició la
  //     videollamada a tiempo (timeout pre-video) → estado vuelve a 'en_espera'
  //     y `medicoId` se limpia (ya no es de este médico).
  useEffect(() => {
    if (!esMedico || videoStarted || modoRevision) return;
    if (!firestoreConsulta || canceladaRedirRef.current) return;
    const est = firestoreConsulta.estado;
    const yaNoEsMia =
      est === 'en_espera' ||
      (!!firestoreConsulta.medicoId && firestoreConsulta.medicoId !== user?.uid);
    const terminal = est === 'finalizada' || est === 'cancelada';
    if (!terminal && !yaNoEsMia) return;
    canceladaRedirRef.current = true;
    const porPaciente =
      firestoreConsulta.disposicion === 'cancelada_paciente' ||
      (!!firestoreConsulta.canceladaPor && firestoreConsulta.canceladaPor === firestoreConsulta.pacienteUid);
    const devueltoAColaPreVideo = est === 'en_espera';
    addToast(
      'info',
      devueltoAColaPreVideo
        ? 'El paciente volvió a la cola porque no se inició la videollamada a tiempo. Puede tomar a otro paciente.'
        : porPaciente
          ? 'El paciente canceló la solicitud y salió de la sala de espera.'
          : 'La consulta ya no está disponible. Puede tomar a otro paciente.',
    );
    // A la LISTA de salas (no a la cola de la sala): entrar a la cola dispara
    // medicoEntrarSala → auto-asignación, que podía re-asignarle de inmediato
    // el mismo paciente recién devuelto.
    navigate('/salas');
  }, [esMedico, videoStarted, modoRevision, firestoreConsulta?.estado,
    firestoreConsulta?.disposicion, firestoreConsulta?.canceladaPor,
    firestoreConsulta?.pacienteUid, firestoreConsulta?.medicoId, firestoreConsulta?.salaId,
    firestoreConsulta, navigate, addToast, user?.uid]);

  const handleExtenderSesion = useCallback(
    async (minutos: 5 | 10 | 15) => {
      if (!consultaId || extendBusy) return;
      setExtendBusy(true);
      setExtendError(null);
      try {
        await extenderSesion(consultaId, minutos);
        setShowExtendMenu(false);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'No se pudo extender';
        setExtendError(msg);
      } finally {
        setExtendBusy(false);
      }
    },
    [consultaId, extendBusy],
  );

  const { flushNow: flushTranscript } = useTranscriptPersist({
    consultaId: consultaReady ? consultaId : undefined,
    segments: transcript,
    flushIntervalMs: 20000,
    flushBatchSize: 6,
    enabled: transcriptionEnabled,
  });

  /* ── Session expiry: 2-min warning, auto-finalize, extension reset ── */

  // Show 2-minute warning once per session (resets if doctor extends time)
  useEffect(() => {
    if (
      sessionTimer.remainingSec <= 120 &&
      sessionTimer.remainingSec > 0 &&
      sessionTimer.endsAt !== null &&
      !expiryWarningDismissed &&
      !showEndCallModal &&
      !isFinalizando &&
      videoStarted
    ) {
      setShowExpiryWarning(true);
    }
  }, [sessionTimer.remainingSec, sessionTimer.endsAt, expiryWarningDismissed, showEndCallModal, isFinalizando, videoStarted]);

  // Reset warning dismissal when time is extended (remainingSec jumps back above 120)
  useEffect(() => {
    if (sessionTimer.remainingSec > 120) {
      setExpiryWarningDismissed(false);
    }
  }, [sessionTimer.remainingSec]);

  // Auto-open finalization dialog when session expires
  useEffect(() => {
    if (
      sessionTimer.warningLevel === 'expired' &&
      videoStarted &&
      !showEndCallModal &&
      !isFinalizando
    ) {
      setShowExpiryWarning(false);
      setShowEndCallModal(true);
    }
  }, [sessionTimer.warningLevel, videoStarted, showEndCallModal, isFinalizando]);

  const clinicalGuardSoap = useMemo(() => {
    const s = liveSoap.soap;
    return { s: s.s, o: s.o, a: s.a, p: s.p };
  }, [liveSoap.soap]);
  const clinicalGuard = useClinicalGuard(patientContext, liveSoap.fhir, clinicalGuardSoap);

  /* Recover persisted transcript batches on mount/re-entry */
  const transcriptRecoveredRef = useRef(false);
  useEffect(() => {
    if (!consultaId || !consultaReady || transcriptRecoveredRef.current) return;
    transcriptRecoveredRef.current = true;
    (async () => {
      try {
        const q = query(
          collection(db, 'consultas', consultaId, 'transcript_live'),
          orderBy('batchSeq', 'asc'),
        );
        const snap = await getDocs(q);
        if (snap.empty) return;
        const recovered: Array<{ id: number; text: string; speaker: string; timestamp: Date; isFinal: boolean }> = [];
        snap.docs.forEach(d => {
          const data = d.data();
          const segs = data.segments as Array<{ id: string | number; text: string; speaker: string; ts: number; isFinal: boolean }> | undefined;
          (segs || []).forEach(s => {
            recovered.push({
              id: typeof s.id === 'number' ? s.id : parseInt(String(s.id), 10) || Date.now() + Math.random(),
              text: s.text || '',
              speaker: s.speaker || 'Desconocido',
              timestamp: new Date(s.ts || Date.now()),
              isFinal: s.isFinal !== false,
            });
          });
        });
        if (recovered.length > 0) {
          loadSegments(recovered);
          console.log(`[Transcript] Recovered ${recovered.length} segments from ${snap.size} batches`);
        }
      } catch (err) {
        console.warn('[Transcript] Failed to recover persisted batches:', err);
      }
    })();
  }, [consultaId, consultaReady, loadSegments]);

  /* Effects */
  const autoAceptarDoneRef = useRef(false);
  useEffect(() => {
    if (modoRevision) return; // en revisión no se toma la consulta
    if (!consultaId || !firestoreConsulta || autoAceptarDoneRef.current) return;
    if (!esMedico) return;
    if (firestoreConsulta.estado !== 'en_espera') return;
    if (firestoreConsulta.medicoId && firestoreConsulta.medicoId === user?.uid) return;
    // Si ESTE médico ya devolvió la consulta a la cola, NO la re-tome
    // automáticamente. Sin esto, al "Devolver a la cola" la consulta pasa a
    // en_espera y este mismo efecto la volvía a tomar (la dejaba ligada al
    // médico → "Consulta en curso"). La toma manual desde la cola sigue válida.
    if (Array.isArray(firestoreConsulta.devueltaPor) && user?.uid && firestoreConsulta.devueltaPor.includes(user.uid)) return;
    autoAceptarDoneRef.current = true;
    aceptarConsultaSvc(consultaId)
      .then(() => {
        invalidateLivekitTokenCache(consultaId);
      })
      .catch((err) => {
        console.error('[VideoCall] auto-aceptar falló:', err);
        autoAceptarDoneRef.current = false;
      });
  }, [consultaId, firestoreConsulta, user?.uid, esMedico, modoRevision]);

  useEffect(() => {
    if (!consultaId || !videoStarted) return;
    if (firestoreConsulta) {
      if (['finalizada', 'cancelada'].includes(firestoreConsulta.estado as string)) {
        console.log('[VideoCall] Consulta en estado terminal, no se pide LiveKit token', { estado: firestoreConsulta.estado });
        setLivekitError('La consulta ya finalizó.');
        return;
      }
      if (firestoreConsulta.estado === 'en_espera' && !firestoreConsulta.medicoId && esMedico) {
        console.log('[VideoCall] Esperando aceptarConsulta antes de pedir token');
        return;
      }
    }
    fetchLivekitToken(consultaId)
      .then(d => {
        setLivekitToken(d.token);
        setLivekitUrl(d.url);
        setLivekitError(null);
        // Sin valor del servidor se asume 'all': si algo falla al resolverla,
        // es mejor dejar que ICE busque ruta que bloquearlo todo tras el TURN.
        setIcePolicy(d.iceTransportPolicy ?? 'all');
        // El backend sondeó el servidor de video y no respondió. El token vino
        // igual (una caída de un instante se resuelve sola), pero si el cliente
        // se queda pegado en "Conectando…" hay que decirle al médico por qué.
        setAvisoConexion(d.advertencia?.texto ?? null);
      })
      .catch(err => {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[VideoCall] fetchLivekitToken falló:', msg);
        setLivekitError(msg);
      });
  }, [consultaId, firestoreConsulta?.estado, firestoreConsulta?.medicoId, esMedico, videoStarted, reconnectNonce]);

  /* ── Reconexión manual del médico desde la pantalla de atención (M6) ──
     Invalida el token cacheado, lo vuelve a pedir fresco y fuerza un remount
     de <LiveKitRoom> (vía key=reconnectNonce). El médico ya no tiene que
     "echar hacia atrás" para volver a la llamada. */
  const handleManualReconnect = useCallback(() => {
    if (!consultaId) return;
    // Candado compartido con el automático: sin esto, un clic justo cuando el watchdog ya
    // había forzado un join disparaba un SEGUNDO remount antes de que el primero terminara de
    // conectar, y las dos sesiones competían por la misma identidad.
    const ahora = Date.now();
    if (ahora - ultimoRemontajeMsRef.current < ESPERA_MIN_ENTRE_REMONTAJES_MS) return;
    ultimoRemontajeMsRef.current = ahora;
    invalidateLivekitTokenCache(consultaId);
    setLivekitError(null);
    setReconnectNonce(n => n + 1);
    logClient({
      nivel: 'WARN', categoria: 'LLAMADA', codigo: 'LLAMADA_REINTENTO',
      componente: 'VideoCallPageV2', consultaId, resultado: 'reintento',
      mensaje: 'Reintento manual de reconexión',
    });
  }, [consultaId]);

  /* ── JOIN FRESCO AUTOMÁTICO (fix bucle de resume) ──
     Disparado por <AutoReconnectWatchdog> cuando la conexión lleva demasiado
     tiempo en problemas: mismo mecanismo que el reintento manual (token fresco
     + remount de <LiveKitRoom>), pero automático y con tope de intentos para
     no entrar en un loop propio. Superado el tope, queda el botón manual del
     ReconnectionOverlay. */
  /* Reintentos con espera creciente, sin rendirse.
   *
   * Antes se topaba en 3 intentos, disparados a los 15 s: contra un servidor
   * que tarda cerca de una HORA en volver —la caída diaria de las 07:04— los
   * tres se agotaban en el primer minuto y quedaba solo el botón manual. En las
   * pruebas del 21-ago eso se tradujo en ocho reintentos a mano seguidos.
   *
   * Ahora la espera crece: 15 s, 30 s, 1 min, 2 min y de ahí en adelante cada
   * 3 minutos, mientras la consulta siga viva. Así la llamada se reanuda sola
   * cuando el servidor regresa, sin machacarlo mientras está caído. */
  const REJOIN_ESPERAS_MS = [15_000, 30_000, 60_000, 120_000];
  const REJOIN_ESPERA_MAX_MS = 180_000;
  const autoRejoinCountRef = useRef(0);
  const proximoRejoinRef = useRef(0);

  const handleAutoRejoin = useCallback(() => {
    if (!consultaId) return;
    // Respetar la espera que corresponde al intento actual.
    if (Date.now() < proximoRejoinRef.current) return;
    // Mismo candado que el reintento manual: si el médico ya disparó un remount hace
    // instantes, esperar en vez de encimar otro.
    const ahora = Date.now();
    if (ahora - ultimoRemontajeMsRef.current < ESPERA_MIN_ENTRE_REMONTAJES_MS) return;
    ultimoRemontajeMsRef.current = ahora;
    const i = autoRejoinCountRef.current;
    proximoRejoinRef.current =
      ahora + (REJOIN_ESPERAS_MS[i] ?? REJOIN_ESPERA_MAX_MS);
    autoRejoinCountRef.current += 1;
    logClient({
      nivel: 'WARN', categoria: 'LLAMADA', codigo: 'LLAMADA_REJOIN_AUTO',
      componente: 'VideoCallPageV2', consultaId, resultado: 'reintento',
      mensaje: `Join fresco automático #${autoRejoinCountRef.current} tras reconexión estancada; próximo intento en ${Math.round((proximoRejoinRef.current - ahora) / 1000)}s`,
    });
    invalidateLivekitTokenCache(consultaId);
    setLivekitError(null);
    setReconnectNonce(n => n + 1);
  }, [consultaId]);

  /* La otra cara del mismo fallo. Si el «resume» deja la sesión a medias, no
     solo se pierde lo que publicamos: también las SUSCRIPCIONES. El médico
     sigue conectado, el paciente sigue publicando, y aun así su recuadro queda
     en negro — que es justo lo que se vio el 25-ago. La etiqueta «sin señal ·
     reconectando» ya lo decía, pero no escalaba a nada y había que esperar a
     que algo más rompiera la sesión.
     Aquí, si la señal del paciente no vuelve por sí sola en este margen, se
     pide un join fresco. La escala de esperas de handleAutoRejoin evita
     machacar cuando el problema está del lado del paciente y no se arregla
     reconectando a este. */
  const SIN_SENAL_ANTES_DE_REJOIN_MS = 20_000;
  const sinSenalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePacienteSinSenal = useCallback((sinSenal: boolean) => {
    if (sinSenalTimerRef.current) {
      clearTimeout(sinSenalTimerRef.current);
      sinSenalTimerRef.current = null;
    }
    if (!sinSenal) return;
    sinSenalTimerRef.current = setTimeout(() => {
      logClient({
        nivel: 'WARN', categoria: 'LLAMADA', codigo: 'LLAMADA_SIN_SENAL_PACIENTE',
        componente: 'VideoCallPageV2', consultaId, resultado: 'reintento',
        mensaje: `El paciente publica pero no llega su medio desde hace ${SIN_SENAL_ANTES_DE_REJOIN_MS / 1000}s; se fuerza un join fresco`,
      });
      handleAutoRejoin();
    }, SIN_SENAL_ANTES_DE_REJOIN_MS);
  }, [consultaId, handleAutoRejoin]);

  useEffect(() => () => {
    if (sinSenalTimerRef.current) clearTimeout(sinSenalTimerRef.current);
  }, []);

  /* Apagar la cámara MUTEA la publicación, no la despublica: desde el servidor
     es indistinguible de una pista que se rompió y dejó de dar imagen. Las dos
     cosas se ven igual —publicada, nunca despublicada, sin video para el
     paciente y con el recuadro propio vacío— y en el incidente del 27-ago no
     hubo forma de saber cuál de las dos había sido. Registrarlo aquí, que es
     donde está la intención del médico, lo resuelve. */
  const alternarCamara = useCallback(() => {
    setIsCameraOn((prev) => {
      logClient({
        nivel: 'INFO', categoria: 'LLAMADA',
        codigo: prev ? 'MEDICO_APAGA_CAMARA' : 'MEDICO_ENCIENDE_CAMARA',
        componente: 'VideoCallPageV2', consultaId, resultado: 'ok',
        mensaje: prev ? 'El médico apagó su cámara' : 'El médico encendió su cámara',
      });
      return !prev;
    });
  }, [consultaId]);

  const handleConnectionStable = useCallback(() => {
    // Conexión sana: se reinicia el contador Y la espera, para que una caída
    // posterior vuelva a reaccionar en 15 s y no siga en la escala anterior.
    autoRejoinCountRef.current = 0;
    proximoRejoinRef.current = 0;
  }, []);

  // OBS: transcripción iniciada al arrancar la llamada (una vez por sesión).
  useEffect(() => {
    if (videoStarted && transcriptionEnabled && consultaId) {
      logClient({
        nivel: 'INFO', categoria: 'TRANSCRIPCION', codigo: 'TRANSCRIPCION_INICIA',
        componente: 'VideoCallPageV2', consultaId, resultado: 'ok',
        mensaje: 'Transcripción iniciada con la llamada',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoStarted]);

  // REC-01: el médico acepta el consentimiento HIPAA → arranca el video y la
  // grabación. La grabación es BEST-EFFORT: si falla (bucket/egress no listo),
  // el video continúa igual y el fallo queda registrado en el visor.
  const handleConsentAccept = useCallback(() => {
    setShowConsent(false);
    setVideoStarted(true);
    logClient({
      nivel: 'INFO', categoria: 'LLAMADA', codigo: 'LLAMADA_MEDICO_INICIA',
      componente: 'VideoCallPageV2', consultaId: consultaId ?? undefined,
      resultado: 'ok', mensaje: 'Médico inició la videollamada',
    });
    if (consultaId) {
      iniciarGrabacion(consultaId)
        .then((res) => { if (!res?.grabacionDeshabilitada) setGrabacionActiva(true); })
        .catch((err) => {
          logClient({
            nivel: 'ERROR', categoria: 'LLAMADA', codigo: 'GRABACION_ERROR',
            componente: 'VideoCallPageV2', consultaId, resultado: 'error',
            mensaje: 'No se pudo iniciar la grabación de la consulta',
            error: { mensaje: err instanceof Error ? err.message : String(err) },
          });
        });
    }
  }, [consultaId]);

  /* ── CITA SEIS — crear la cita al tomar al paciente ──
     Best-effort: si falla, muestra un banner con "Reintentar" pero el médico
     puede continuar la consulta. La CF es idempotente (no reenvía si ya se
     creó por otro médico o antes de devolver a la cola) y respeta el parámetro
     de configuración (si está apagado, marca la cita como "no enviada"). */
  const enviarCitaSeis = useCallback(async () => {
    if (!consultaId || citaSeisEnviando) return;
    setCitaSeisEnviando(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const fn = httpsCallable<
        { consultaId: string; idToken?: string },
        { ok?: boolean; yaEnviada?: boolean; deshabilitada?: boolean; error?: string }
      >(functions, 'portal_enviarCitaSeis');
      const res = await fn({ consultaId, idToken });
      const d = res.data;
      if (d.ok === false || d.error) {
        setCitaSeisError(d.error || 'No se pudo crear la cita en HCIS.');
        logClient({
          nivel: 'ERROR', categoria: 'CITA', codigo: 'SEIS_CITA_ERROR',
          componente: 'VideoCallPageV2', consultaId, resultado: 'error',
          mensaje: d.error || 'Fallo al crear la cita en HCIS',
        });
      } else {
        setCitaSeisError(null); // enviada, ya enviada o deshabilitada
      }
    } catch (err) {
      setCitaSeisError('No se pudo crear la cita en HCIS. Puede reintentar.');
      logClient({
        nivel: 'ERROR', categoria: 'CITA', codigo: 'SEIS_CITA_ERROR',
        componente: 'VideoCallPageV2', consultaId: consultaId ?? undefined, resultado: 'error',
        mensaje: 'Excepción llamando portal_enviarCitaSeis',
        error: { mensaje: err instanceof Error ? err.message : String(err) },
      });
    } finally {
      setCitaSeisEnviando(false);
    }
  }, [consultaId, citaSeisEnviando]);

  /* Dispara la creación de la cita SEIS UNA vez cuando el médico tratante toma
     al paciente (consulta en_atencion y suya) y arrancó el video. La idempotencia
     real vive en el backend; este ref sólo evita repetir dentro de la sesión. */
  useEffect(() => {
    if (citaSeisIntentadaRef.current) return;
    if (modoRevision || !esMedico || !videoStarted) return;
    if (!firestoreConsulta || firestoreConsulta.medicoId !== user?.uid) return;
    if (firestoreConsulta.estado !== 'en_atencion') return;
    // Ya creada previamente (retomada) → no reintentar ni mostrar banner.
    if (firestoreConsulta.citaSeis?.estado === 'enviada') { citaSeisIntentadaRef.current = true; return; }
    citaSeisIntentadaRef.current = true;
    enviarCitaSeis();
  }, [modoRevision, esMedico, videoStarted, firestoreConsulta?.medicoId, firestoreConsulta?.estado, firestoreConsulta?.citaSeis?.estado, user?.uid, enviarCitaSeis]);

  /* Al desconectarse de LiveKit: persistir la transcripción acumulada (M7) e
     invalidar el token para que un reintento obtenga uno fresco. La sala NO se
     cierra: el médico permanece en la pantalla y puede reintentar. */
  /* Motivo por el que LiveKit nos sacó de la sala, cuando hay que decírselo al médico.
     `DUPLICATE_IDENTITY` significa que otra ventana entró a ESTA consulta con la misma
     identidad y el servidor expulsó a esta. Pasó el 04-09-2026 y el médico no se enteró: la
     sesión que quedó viva no publicó cámara ni micrófono y el paciente estuvo tres minutos
     con la pantalla muda sin que nadie dijera nada. */
  const [expulsadoPorDuplicado, setExpulsadoPorDuplicado] = useState(false);

  const handleLivekitDisconnected = useCallback((motivo?: DisconnectReason) => {
    if (consultaId) invalidateLivekitTokenCache(consultaId);
    flushTranscript();
    if (motivo === DisconnectReason.DUPLICATE_IDENTITY) {
      setExpulsadoPorDuplicado(true);
      logClient({
        nivel: 'WARN', categoria: 'LLAMADA', codigo: 'LLAMADA_IDENTIDAD_DUPLICADA',
        componente: 'VideoCallPageV2', consultaId, resultado: 'error',
        mensaje: 'Otra ventana entró a esta consulta y el servidor expulsó a esta sesión',
        detalle: { intentoConexion: reconnectNonce },
      });
    }
  }, [consultaId, flushTranscript, reconnectNonce]);

  /* ── M8 — Primer corte: "Terminar llamada con paciente" ──
     Corta el video (la CF cierra la sala → detiene facturación y libera al
     paciente), pero la consulta sigue en_atencion y el médico permanece en la
     pantalla para editar la nota y cerrarla cuando quiera. */
  /* Detener/reanudar la TRANSCRIPCIÓN a petición del médico (botón o tecla T).
     El flush de lo ya capturado al detener lo hace un efecto centralizado
     (ver "flush al detener transcripción"). */
  const handleToggleTranscripcion = useCallback(() => {
    setTranscriptionEnabled((prev) => !prev);
  }, []);

  /* Flush al detener transcripción: sin importar CÓMO se detuvo (botón del
     toolbar, banner o tecla T), lo dicho HASTA ese momento se persiste
     (transcript crudo) y se procesa (buffer de IA) para que sí entre en la
     nota. A partir de ahí no se transcribe, no se generan notas y no se guarda
     la conversación — el banner visible lo indica. */
  const prevTransEnabledRef = useRef(true);
  useEffect(() => {
    if (prevTransEnabledRef.current && !transcriptionEnabled) {
      try { flushTranscript(); } catch { /* best-effort */ }
      medicalNotes.flushPending().catch(() => { /* best-effort */ });
    }
    prevTransEnabledRef.current = transcriptionEnabled;
  }, [transcriptionEnabled, flushTranscript, medicalNotes]);

  const handleTerminarLlamada = useCallback(async () => {
    if (!consultaId || isEndingCall) return;
    const ok = window.confirm(
      'Terminar la llamada con el paciente\n\n' +
      'Se cerrará la videollamada y el paciente quedará liberado (no podrá volver a unirse). ' +
      'Usted permanecerá en la pantalla para editar la nota médica y, cuando termine, finalizar la atención.\n\n' +
      '¿Continuar?'
    );
    if (!ok) return;
    setIsEndingCall(true);
    try {
      /* ── PRIMERO avisar al backend, DESPUÉS soltar el video ──
         El orden importa y antes estaba al revés: se desmontaba `<LiveKitRoom>`
         de inmediato y recién tras `flushPending()` —que espera a la IA, con
         techo de 30 s— se llamaba a `terminarLlamada`, que es lo que marca
         `videoFinalizado`. Mientras tanto el médico ya había salido del room, y
         la sala se cierra sola 20 s después del último participante
         (`departureTimeout`). Cuando la IA tardaba más que esa ventana, LiveKit
         emitía `room_finished` con `videoFinalizado` todavía en falso y el
         webhook lo interpretaba como AVERÍA: devolvía el paciente a la cola,
         le mandaba un aviso de "falla técnica" y el médico —que seguía en su
         pantalla— perdía la consulta, que ya la había tomado otro.
         (Ocurrió el 3-ago-2026 a las 11:18 con la consulta Mnh1Y9…_20260803.)

         Ahora la intención se persiste antes de soltar nada: son unos cientos
         de milisegundos y cierran la ventana de la carrera. */
      flushTranscript();
      const fn = httpsCallable<
        { consultaId: string },
        { success: boolean; aplicado?: boolean; motivo?: string }
      >(functions, 'portal_terminarLlamada');
      const res = await fn({ consultaId });

      // Con el corte ya registrado, soltar el video es seguro: el desmonte
      // intencional evita el bucle de reconexión del cliente.
      setVideoCorteLocal(true);

      if (res.data.aplicado === false) {
        // La consulta ya no estaba en atención (otro médico la tomó, o el
        // sistema la cerró). Antes esto era un no-op silencioso y el médico se
        // quedaba mirando una pantalla que ya no correspondía a nada.
        setLivekitError(
          res.data.motivo
          ?? 'Esta consulta ya no está activa: fue cerrada o la tomó otro médico.',
        );
      }

      // Procesar el último tramo de conversación que quedó en el buffer de IA.
      // Va DESPUÉS del corte: el agente ya no recibe audio nuevo, así que este
      // trabajo no compite con nada y puede tardar lo que necesite.
      await medicalNotes.flushPending();
      // El snapshot de Firestore (videoFinalizado=true) activará videoDisabled
      // y la UI cambiará a modo edición de nota.
    } catch (err) {
      console.error('[VideoCall] terminarLlamada error:', err);
      // Si el aviso al backend falló, NO se suelta el video en silencio: sin
      // esa marca, soltarlo es justamente lo que dispara el falso fallo técnico.
      setLivekitError(
        err instanceof Error
          ? `No se pudo terminar la llamada: ${err.message}. Intente de nuevo.`
          : 'No se pudo terminar la llamada. Intente de nuevo.',
      );
    } finally {
      setIsEndingCall(false);
    }
  }, [consultaId, isEndingCall, flushTranscript, medicalNotes]);

  /* Devolver la consulta a la cola ANTES de iniciar la videollamada: el paciente
     vuelve a la sala para que otro médico lo tome. */
  const [devolviendo, setDevolviendo] = useState(false);
  const handleDevolverACola = useCallback(async () => {
    if (!consultaId || devolviendo) return;
    const ok = window.confirm(
      'Devolver el paciente a la cola\n\n' +
      'La consulta volverá a la sala de espera para que otro médico la tome. ' +
      'Esta acción solo es posible antes de iniciar la videollamada.\n\n¿Continuar?'
    );
    if (!ok) return;
    setDevolviendo(true);
    try {
      const fn = httpsCallable<{ consultaId: string }, { success: boolean }>(functions, 'portal_devolverConsultaACola');
      await fn({ consultaId });
      // A la LISTA de salas, no a la cola de la sala del paciente: entrar a la
      // cola dispara medicoEntrarSala → auto-asignación, que podía devolverle
      // al médico EL MISMO paciente que acaba de regresar.
      navigate('/salas');
    } catch (err) {
      console.error('[VideoCall] devolverConsultaACola error:', err);
      const msg = err instanceof Error ? err.message : 'No se pudo devolver la consulta a la cola.';
      window.alert(msg);
      setDevolviendo(false);
    }
  }, [consultaId, devolviendo, navigate]);

  /* Atender por teléfono: la videollamada no se pudo establecer y el médico
     resuelve la consulta por llamada normal. Quita el deadline pre-video (que
     devolvería al paciente a la cola en plena llamada) y deja la consulta en
     atención para cerrarla con el flujo de siempre. */
  const [pasandoATelefonica, setPasandoATelefonica] = useState(false);
  const handleAtenderSinVideo = useCallback(async () => {
    if (!consultaId || pasandoATelefonica) return;
    const ok = window.confirm(
      'Atender por llamada telefónica\n\n' +
      'Use esta opción si la videollamada no se pudo establecer y atenderá al ' +
      'paciente por teléfono.\n\n' +
      'El paciente dejará de contar con el tiempo para iniciar el video (no ' +
      'volverá a la cola) y usted podrá escribir la nota y finalizar la ' +
      'atención normalmente.\n\n¿Continuar?'
    );
    if (!ok) return;
    setPasandoATelefonica(true);
    try {
      const fn = httpsCallable<{ consultaId: string; motivo: string }, { success: boolean }>(
        functions, 'portal_atenderSinVideo',
      );
      await fn({ consultaId, motivo: 'fallo_video_paciente' });
      setAtencionTelefonica(true);
    } catch (err) {
      console.error('[VideoCall] atenderSinVideo error:', err);
      window.alert(err instanceof Error ? err.message : 'No se pudo cambiar a atención telefónica.');
    } finally {
      setPasandoATelefonica(false);
    }
  }, [consultaId, pasandoATelefonica]);

  /* Volver a la videollamada tras haber pasado a atención telefónica: se
     restaura el deadline pre-video, así que la consulta vuelve a comportarse
     como una teleconsulta normal. */
  const handleVolverAVideo = useCallback(async () => {
    if (!consultaId || pasandoATelefonica) return;
    setPasandoATelefonica(true);
    try {
      const fn = httpsCallable<{ consultaId: string; revertir: boolean }, { success: boolean }>(
        functions, 'portal_atenderSinVideo',
      );
      await fn({ consultaId, revertir: true });
      setAtencionTelefonica(false);
    } catch (err) {
      console.error('[VideoCall] volver a video error:', err);
      window.alert(err instanceof Error ? err.message : 'No se pudo volver a la videollamada.');
    } finally {
      setPasandoATelefonica(false);
    }
  }, [consultaId, pasandoATelefonica]);

  /* Aquí había una «comprobación» de cámara que ABRÍA LA CÁMARA UNA SEGUNDA VEZ
     —con getUserMedia a 640x480— y acto seguido hacía `stop()` sobre esa toma.
     Corría a la vez que LiveKit abría la cámara para publicar a 960x540.

     Dos aperturas simultáneas del mismo dispositivo con restricciones distintas
     obligan al navegador a reconfigurar la fuente compartida, y el `stop()`
     posterior puede dejar la pista ya publicada sin entregar un solo frame. El
     resultado es exactamente lo que se reportó el 27-ago: la pista de video del
     médico PUBLICADA —el servidor la ve, se emite `track_published`— pero negra
     en su propio recuadro y ausente en la app del paciente, con el audio
     perfecto porque el sondeo pedía `audio: false`.

     Que dependa de una carrera y del driver de cada cámara explica por qué
     llevaba meses ahí sin dar la cara todos los días.

     No hace falta abrir nada para saber si la cámara funciona: si LiveKit tiene
     la publicación con su pista, funciona. Eso ya lo comprueba `MediaSync`, que
     es la única fuente de verdad y no toca el dispositivo. */

  useEffect(() => () => clearBackgroundCache(), []);

  useEffect(() => {
    if (!videoStarted) return;
    timerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [videoStarted]);

  const motivoProcessedRef = useRef(false);
  useEffect(() => {
    if (motivoProcessedRef.current) return;
    if (!consultaId || notaCerrada || modoRevision) return;
    const motivo = firestoreConsulta?.motivoConsulta?.trim();
    if (!motivo) return;
    motivoProcessedRef.current = true;
    medicalNotes.processMotivo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultaId, firestoreConsulta?.motivoConsulta]);
  useEffect(() => { if (transcript.length > 0) medicalNotes.addTranscriptSegments(transcript); }, [transcript]); // eslint-disable-line
  useEffect(() => { transcriptionBodyRef.current?.scrollTo({ top: transcriptionBodyRef.current.scrollHeight, behavior: 'smooth' }); }, [transcript, interimText]);
  // Auto-scroll de la caption bar al final (últimas intervenciones siempre visibles)
  useEffect(() => { captionBodyRef.current?.scrollTo({ top: captionBodyRef.current.scrollHeight, behavior: 'smooth' }); }, [transcript, interimText, captionOpen]);

  /* Escape cierra el selector de fondo. Es la única tecla que la consulta
     escucha de forma global, y a propósito: Escape nunca es un carácter, así
     que no puede desaparecer de la nota como pasó con M, T y V. Los modales
     de finalizar y de aviso de tiempo traen su propio Escape (ui/Modal). */
  useEffect(() => {
    if (!showBgPicker) return;
    const alEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowBgPicker(false); };
    window.addEventListener('keydown', alEscape);
    return () => window.removeEventListener('keydown', alEscape);
  }, [showBgPicker]);

  /* PiP drag — temporarily unused while layout is being refined */

  /* Resize: horizontal (col-main ↔ col-side) */
  const handleHResizeDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = sideWidth;
    const onMove = (ev: MouseEvent) => {
      const delta = startX - ev.clientX;
      // El máximo reserva espacio para el panel del copiloto (si está abierto)
      // y un mínimo para el video, así ninguna columna se sale de la pantalla.
      const maxSide = window.innerWidth - (copilotOpen ? copilotWidth : 0) - PANEL_OVERHEAD;
      setSideWidth(Math.min(Math.max(startW + delta, 240), Math.max(240, maxSide)));
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [sideWidth, copilotOpen, copilotWidth]);

  /* Resize: panel del copiloto (arrastrar su borde izquierdo) */
  const handleCopilotResizeDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = copilotWidth;
    const onMove = (ev: MouseEvent) => {
      const delta = startX - ev.clientX; // arrastrar a la izquierda → más ancho
      // Con video visible se reserva pestañas + mínimo de video; sin video, solo
      // un mínimo para el panel de pestañas (que se hace flexible).
      const maxCopilot = videoOpen
        ? window.innerWidth - sideWidth - PANEL_OVERHEAD
        : window.innerWidth - 300;
      setCopilotWidth(Math.min(Math.max(startW + delta, 300), Math.max(300, maxCopilot)));
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [copilotWidth, sideWidth, videoOpen]);

  // Al abrir el copiloto, reducir el panel de pestañas si no caben ambos.
  useEffect(() => {
    if (!copilotOpen) return;
    const maxSide = window.innerWidth - copilotWidth - PANEL_OVERHEAD;
    setSideWidth((w) => Math.min(w, Math.max(240, maxSide)));
  }, [copilotOpen, copilotWidth]);

  /* Vertical resize — temporarily unused while layout is being refined */

  /* Derived */
  /* Una sola ventana por consulta: dos conexiones con la misma identidad hacen que LiveKit
     expulse a la primera, y la que queda puede terminar sin publicar nada (04-09-2026). */
  const { bloqueado: otraVentanaTieneLaSala, tomarControl } = useSalaExclusiva(consultaId);
  const livekitReady = !!livekitToken && !!livekitUrl && !livekitError
    && otraVentanaTieneLaSala === false && !expulsadoPorDuplicado;
  const { isProcessing, error: aiError } = medicalNotes;

  // ── AI pipeline status: listening → queued → processing → idle ──
  const hasPendingBuffer = medicalNotes.bufferLength > 0;
  const isListening = transcriptionEnabled && !!interimText;
  const aiStatus: 'listening' | 'queued' | 'processing' | 'idle' =
    isProcessing ? 'processing' :
    hasPendingBuffer ? 'queued' :
    isListening ? 'listening' :
    'idle';

  // En modo revisión, el editor se siembra con la nota persistida (si existe).
  const soap = (modoRevision && revisionSeed) ? revisionSeed : liveSoap.soap;
  const baseFhir = liveSoap.fhir;
  const baseEducation = liveSoap.healthEducation;

  // ── NU: persistencia de las ediciones del médico en el documento único ──
  // Toda mutación manual (SOAP tecleado, tarjetas del plan, educación) va a
  // portal_editarNota; el servidor marca la sección como edición humana para
  // que los ciclos de IA no la pisen. En modo revisión NO se persiste aquí
  // (ese flujo usa portal_editarConsultaHistorial con bitácora).
  const puedePersistirNota = consultaReady && !modoRevision && !notaCerrada;
  const persistirNotaRef = useRef(puedePersistirNota);
  persistirNotaRef.current = puedePersistirNota;
  const llamarEditarNota = useCallback((payload: Record<string, unknown>) => {
    if (!persistirNotaRef.current || !consultaId) return;
    const fn = httpsCallable(functions, 'portal_editarNota');
    fn({ consultaId, ...payload }).catch((err) => {
      console.warn('[VideoCall] No se pudo persistir la edición de la nota:', err);
    });
  }, [consultaId]);

  // Capa de CURACIÓN del plan estructurado (borrar/editar/agregar manual)
  // con overlay optimista; cada mutación se persiste vía portal_editarNota.
  // Los cambios del asistente llegan por el snapshot (se aplican server-side).
  const plan = usePlanCuration(baseFhir, baseEducation, {
    persistir: (acciones) => llamarEditarNota({ fhirAcciones: acciones }),
    persistirEducacion: (texto) => llamarEditarNota({ educacion: texto }),
  });
  const fhir = plan.fhir;
  const aiAlerts = liveSoap.alerts;
  const healthEducation = plan.healthEducation;

  // Incremental SOAP merge: respuesta inmediata local + persistencia debounced;
  // las propuestas de la IA sobre secciones protegidas vienen del documento.
  const {
    localSoap, handleSoapChange, applySoap, pendingAIUpdates,
    acceptAIUpdate, dismissAIUpdate, hasPendingUpdates,
    recentlyUpdated, doctorEdited,
  } = useSoapMerge(soap, activeTab === 'soap', {
    propuestas: liveSoap.propuestasIA,
    persistir: (patch) => llamarEditarNota({ soap: patch }),
    resolverPropuesta: (key, aceptar) =>
      llamarEditarNota(aceptar ? { aceptarPropuesta: key } : { descartarPropuesta: key }),
  });

  /* Copiar el SOAPE COMPLETO al portapapeles, con los encabezados de cada
     sección, para pegarlo en otro sistema. Las secciones vacías se omiten. */
  const [soapeCopiado, setSoapeCopiado] = useState(false);
  const copiadoTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => { if (copiadoTimerRef.current) clearTimeout(copiadoTimerRef.current); }, []);
  const handleCopiarSoape = useCallback(async () => {
    if (!(await copiarSoape(localSoap))) return;
    setSoapeCopiado(true);
    if (copiadoTimerRef.current) clearTimeout(copiadoTimerRef.current);
    copiadoTimerRef.current = setTimeout(() => setSoapeCopiado(false), 1800);
  }, [localSoap]);

  /* Copiar SOLO el fragmento que el médico dejó sombreado (Selection API), sin
     necesidad de copiar la nota completa y editarla manualmente después. */
  const [seleccionEstado, setSeleccionEstado] = useState<'copiado' | 'vacio' | null>(null);
  const seleccionTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => { if (seleccionTimerRef.current) clearTimeout(seleccionTimerRef.current); }, []);
  const handleCopiarSeleccion = useCallback(async () => {
    const ok = await copiarSeleccionActual();
    setSeleccionEstado(ok ? 'copiado' : 'vacio');
    if (seleccionTimerRef.current) clearTimeout(seleccionTimerRef.current);
    seleccionTimerRef.current = setTimeout(() => setSeleccionEstado(null), 1800);
  }, []);

  const allAlerts = [...new Set([...aiAlerts, ...clinicalGuard.alerts.map(a => a.message)])];
  const hasSOAPContent = !!(localSoap.s || localSoap.o || localSoap.a || localSoap.p);

  // Modo revisión: guardar la nota editada (con bitácora) y volver al inicio.
  const handleGuardarRevision = useCallback(async () => {
    if (!consultaId || guardandoRevision) return;
    setGuardandoRevision(true);
    try {
      const fn = httpsCallable<{ consultaId: string; soap: typeof localSoap; origen: string }, { cambios?: number }>(
        functions, 'portal_editarConsultaHistorial',
      );
      await fn({ consultaId, soap: localSoap, origen: 'workspace' });
      navigate('/');
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'No se pudieron guardar los cambios');
    } finally {
      setGuardandoRevision(false);
    }
  }, [consultaId, guardandoRevision, localSoap, navigate]);

  // El procesamiento automático de la transcripción actualiza la nota SOAPE en
  // segundo plano. No lo anunciamos en el chat del asistente (es un canal
  // conversacional); solo marcamos la pestaña con un punto de "sin leer".
  const prevProcessCountRef = useRef(0);
  useEffect(() => {
    if (medicalNotes.processCount > prevProcessCountRef.current && prevProcessCountRef.current > 0) {
      const hasFields = !!(soap.s || soap.o || soap.a || soap.p);
      if (activeTab !== 'soap' && hasFields) setSoapHasUnread(true);
    }
    prevProcessCountRef.current = medicalNotes.processCount;
  }, [medicalNotes.processCount, soap, activeTab]);

  /** Llama la CF y devuelve el texto del resumen para el paciente (o lanza). */
  const fetchResumenPacienteTexto = useCallback(async (): Promise<string> => {
    // Issue #41: "reportado_paciente" es lo que el PACIENTE dijo estar
    // tomando (antecedente), no lo que el médico prescribió en esta
    // consulta — incluirlo aquí hacía que el resumen (y de ahí la lista de
    // medicamentos que ve el paciente) mezclara ambos como si el médico
    // hubiera indicado los dos.
    const medicamentos = fhir
      .filter((f) => f.type === 'medication' && f.prescriptionType !== 'reportado_paciente')
      .map((m) => [m.display, m.presentation, m.dose, m.route, m.frequency,
                   m.duration ? `por ${m.duration}` : '']
        .filter(Boolean).join(' ').trim());
    const ORDER_TYPES = ['labOrder', 'labResult', 'imagingOrder', 'referral', 'procedure'];
    const ordenes = fhir.filter((f) => ORDER_TYPES.includes(f.type)).map((o) => o.display);
    const fn = httpsCallable<
      { pacienteNombre: string; motivoConsulta: string; plan: string; medicamentos: string[]; ordenes: string[]; educacion: string },
      { resumen?: string }
    >(functions, 'portal_generarResumenPaciente');
    const res = await fn({
      pacienteNombre: patient.nombre || '',
      motivoConsulta: patient.motivoConsulta || '',
      plan: localSoap.p || '',
      medicamentos,
      ordenes,
      educacion: healthEducation || '',
    });
    return res.data?.resumen || '';
  }, [fhir, localSoap.p, healthEducation, patient.nombre, patient.motivoConsulta]);

  // Paso visible del overlay de cierre: generando el resumen para el paciente.
  const [generandoResumenCierre, setGenerandoResumenCierre] = useState(false);

  /* Salida con trabajo de IA pendiente: si el médico intenta REGRESAR mientras
     el buffer de notas aún se procesa, se le avisa (puede quedarse a esperar o
     salir sabiendo que la red de seguridad del backend consolidará lo ya
     persistido). El mismo aviso aplica a cerrar/recargar la pestaña. */
  const handleBack = useCallback(() => {
    if (medicalNotes.hasPendingWork() && !isFinalizando) {
      const ok = window.confirm(
        'La nota médica aún se está procesando con IA.\n\n' +
        'Si sale ahora, el último tramo de la conversación podría no quedar en la nota. ' +
        'Se recomienda esperar unos segundos a que termine (vea el indicador "Procesando").\n\n' +
        '¿Salir de todos modos?'
      );
      if (!ok) return;
    }
    navigate(-1);
  }, [medicalNotes, isFinalizando, navigate]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (medicalNotes.hasPendingWork()) {
        e.preventDefault();
        // Chrome requiere returnValue para mostrar el diálogo nativo.
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [medicalNotes]);

  const handleHangUp = useCallback(async (closeData?: {
    disposicion: DisposicionConsulta;
    diagnosticoPrincipal?: { codigo: string; descripcion: string };
    planSeguimiento?: 'ninguno' | '1_semana' | '2_semanas' | '1_mes' | 'especialista' | 'urgencias';
    recetaEmitida?: boolean;
    ordenLabEmitida?: boolean;
    educacionEntregada?: boolean;
    calidadConexion?: number;
    calidadIA?: number;
    tagsFeedback?: string[];
    comentarioMedico?: string;
    rating?: number;
    feedback?: string;
    tags?: string[];
  }) => {
    console.log('[VideoCall] handleHangUp invoked', { consultaId, hasCloseData: !!closeData });
    setShowEndCallModal(false);
    setIsFinalizando(true);
    if (timerRef.current) clearInterval(timerRef.current);

    // Issue #18: marca de inmediato que el médico YA está finalizando —
    // best-effort, no bloquea si falla. Le da a `cerrarSesionesExpiradas`
    // (cron cada minuto, cierra consultas cuyo tiempo máximo se agotó) una
    // señal para no competir por el cierre justo mientras el médico está a
    // mitad de camino guardando la nota/transcripción.
    if (consultaId) {
      updateDoc(doc(db, 'consultas', consultaId), { finalizandoDesde: serverTimestamp() })
        .catch(() => { /* no crítico */ });
    }

    const finalDisposition = closeData?.disposicion || disposition;

    let cfSucceeded = false;
    let batchDocs: Awaited<ReturnType<typeof getDocs>> | null = null;
    let resumenFinal = resumenPaciente;
    if (consultaId) {
      try {
        // Procesar el ÚLTIMO tramo pendiente ANTES de finalizar: espera a que
        // cualquier ciclo en curso termine y vacía el buffer de IA. Así la nota
        // ia_soap final queda persistida y portal_finalizarConsulta la consolida
        // server-side (no se pierde el cierre de la conversación).
        await medicalNotes.flushPending();

        // Resumen para el paciente: si el médico nunca abrió esa pestaña, el
        // resumen estaría vacío y el paciente no vería nada en su app. Para
        // atenciones reales (completada/referida/incompleta) con contenido
        // clínico, se genera automáticamente aquí ANTES de finalizar.
        // Best-effort con techo de 25 s: si la IA falla, se finaliza igual.
        const disposicionAtendida = ['completada', 'referida', 'incompleta'].includes(finalDisposition);
        const hayContenidoClinico = !!(localSoap.p || localSoap.s || fhir.length > 0);
        // Se regenera no solo cuando falta, también cuando el plan cambió
        // después de generarlo: firmar un resumen que ya no coincide con lo
        // recetado es peor que no tenerlo.
        const resumenDesfasado = !resumenEditadoRef.current
          && resumenFirmaRef.current !== firmaClinicaRef.current;
        if ((!resumenFinal.trim() || resumenDesfasado) && disposicionAtendida && hayContenidoClinico) {
          setGenerandoResumenCierre(true);
          try {
            const timeout = new Promise<string>((_, rej) =>
              setTimeout(() => rej(new Error('timeout resumen')), 25000));
            resumenFinal = await Promise.race([fetchResumenPacienteTexto(), timeout]);
            if (resumenFinal) setResumenPaciente(resumenFinal);
          } catch (err) {
            console.warn('[VideoCall] No se pudo generar el resumen del paciente al cierre:', err);
          } finally {
            setGenerandoResumenCierre(false);
          }
        }

        // Consolidate persisted batches + in-memory transcript
        const batchQuery = query(
          collection(db, 'consultas', consultaId, 'transcript_live'),
          orderBy('batchSeq', 'asc'),
        );
        batchDocs = await getDocs(batchQuery);
        const persistedSegments: Array<{ id: number; speaker: string; text: string; ts: number; isFinal: boolean }> = [];
        batchDocs.docs.forEach(d => {
          const data = d.data() as { segments?: Array<{ id: string | number; text: string; speaker: string; ts: number; isFinal: boolean }> };
          const segs = data.segments;
          (segs || []).forEach(s => {
            persistedSegments.push({
              id: typeof s.id === 'number' ? s.id : parseInt(String(s.id), 10) || 0,
              text: (s.text || '').slice(0, 2000),
              speaker: s.speaker || 'Desconocido',
              ts: s.ts || Date.now(),
              isFinal: true,
            });
          });
        });
        // Merge: persisted + any in-memory not yet persisted
        const persistedIds = new Set(persistedSegments.map(s => s.id));
        const onlyInMemory = transcript
          .filter(t => !persistedIds.has(t.id))
          .map(t => ({
            id: t.id, speaker: t.speaker, text: t.text,
            ts: typeof (t as { ts?: number }).ts === 'number' ? (t as { ts?: number }).ts! : Date.now(),
            isFinal: true,
          }));
        const consolidated = [...persistedSegments, ...onlyInMemory].sort((a, b) => a.id - b.id);

        const finalizar = httpsCallable<unknown, { success: boolean; duracionSegundos: number }>(
          functions,
          'portal_finalizarConsulta',
        );
        const soapPayload = { s: localSoap.s || soap?.s || '', o: localSoap.o || soap?.o || '', a: localSoap.a || soap?.a || '', p: localSoap.p || soap?.p || '', e: localSoap.e || soap?.e || '' };
        console.log('[VideoCall] Llamando portal_finalizarConsulta...', {
          consultaId, disposicion: finalDisposition,
          soapHasContent: !!(soapPayload.s || soapPayload.o || soapPayload.a || soapPayload.p),
          transcriptCount: consolidated.length,
          inMemory: transcript.length,
          fromBatches: persistedSegments.length,
        });
        const validRating = (n: number | undefined | null): number | undefined =>
          typeof n === 'number' && n >= 1 && n <= 5 ? n : undefined;
        const ratingConexion = validRating(closeData?.calidadConexion);
        const ratingIA = validRating(closeData?.calidadIA ?? closeData?.rating);

        const res = await finalizar({
          consultaId,
          disposicion: finalDisposition,
          diagnosticoPrincipal: closeData?.diagnosticoPrincipal,
          planSeguimiento: closeData?.planSeguimiento ?? 'ninguno',
          recetaEmitida: !!closeData?.recetaEmitida,
          ordenLabEmitida: !!closeData?.ordenLabEmitida,
          educacionEntregada: !!closeData?.educacionEntregada,
          calidadConexion: ratingConexion,
          calidadIA: ratingIA,
          tagsFeedback: closeData?.tagsFeedback ?? closeData?.tags ?? [],
          comentarioMedico: closeData?.comentarioMedico ?? closeData?.feedback ?? '',
          soapFinal: soapPayload,
          transcript: consolidated,
          calidad: ratingConexion,
          observaciones: closeData?.comentarioMedico ?? closeData?.feedback,
          resumenPaciente: resumenFinal,
        });
        cfSucceeded = true;
        console.log('[VideoCall] portal_finalizarConsulta OK', res.data);

        // La consulta ya estaba cerrada por otra vía (el paciente canceló desde
        // la app, el sistema por tiempo, un administrador). El servidor adjuntó
        // la nota igual, pero la disposición que quedó NO es la que el médico
        // eligió: hay que decírselo, no dar un "guardado" a secas.
        const cierre = res.data as { notaTardia?: boolean; cerradaPor?: string } | undefined;
        if (cierre?.notaTardia) {
          addToast(
            'info',
            cierre.cerradaPor === 'cancelada_paciente'
              ? 'El paciente canceló la consulta antes de que usted la cerrara. Su nota clínica quedó guardada.'
              : 'La consulta ya se había cerrado. Su nota clínica quedó guardada.',
          );
        }

        // Clean up transcript_live batches after successful finalization
        if (batchDocs && !batchDocs.empty) {
          try {
            const batch = writeBatch(db);
            batchDocs.docs.forEach(d => batch.delete(d.ref));
            await batch.commit();
            console.log(`[Transcript] Cleaned up ${batchDocs.size} live batches`);
          } catch (cleanupErr) {
            console.warn('[Transcript] Failed to clean up live batches:', cleanupErr);
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[VideoCall] Error finalizando consulta:', msg, err);

        // Si la consulta ya no está en atención, casi siempre es porque alguien
        // se adelantó: el paciente canceló desde la app, el cron la cerró por
        // tiempo, o un administrador la cerró. Nada de eso es un fallo del
        // médico y su objetivo —cerrar y dejar la nota— ya está cumplido, así
        // que se confirma releyendo el documento en vez de asumir.
        // (El backend ya adjunta la nota en esos casos; esta rama cubre las
        // carreras y las versiones desplegadas más viejas.)
        let yaCerrada = false;
        let cerradaPor: string | undefined;
        if (consultaId && /no est[aá].*en atenci[oó]n/i.test(msg)) {
          try {
            const freshData = (await getDoc(doc(db, 'consultas', consultaId))).data();
            if (freshData?.estado === 'finalizada') {
              yaCerrada = true;
              cerradaPor = freshData?.disposicion as string | undefined;
            }
          } catch { /* si falla la relectura, cae al flujo de confirm() de abajo */ }
        }

        if (yaCerrada) {
          addToast(
            'info',
            cerradaPor === 'cancelada_paciente'
              ? 'El paciente canceló la consulta antes de que usted la cerrara. Lo que alcanzó a escribir quedó registrado.'
              : 'La consulta ya se había cerrado. La nota guardada hasta ese punto quedó registrada.',
          );
          // Continuar a navegación — no hay nada más que cerrar.
        } else
        // eslint-disable-next-line no-alert
        if (window.confirm(
          `No se pudo cerrar la consulta:\n${msg}\n\n`
          + '¿Salir de todos modos? La consulta seguirá abierta y lo que escribió '
          + 'después del último guardado podría no quedar registrado.',
        )) {
          // Continuar a navegación
        } else {
          setIsFinalizando(false);
          setShowEndCallModal(true);
          return;
        }
      }
    }

    if (!cfSucceeded) {
      console.warn('[VideoCall] Consulta no se finalizó en backend, pero navegando de todos modos.');
    }

    setIsFadingOut(true);
    if (consultaId) invalidateLivekitTokenCache(consultaId);

    setTimeout(() => {
      // Al finalizar la consulta el médico vuelve al listado de salas.
      navigate('/salas');
    }, 500);
  }, [navigate, consultaId, disposition, callDuration, soap, localSoap, transcript, medicalNotes, resumenPaciente, fhir.length, fetchResumenPacienteTexto]);

  // Envía una instrucción/pregunta al ASISTENTE médico. El asistente recibe el
  // contexto completo de la consulta (paciente, SOAPE, medicamentos, órdenes,
  // educación y transcripción), responde dudas clínicas y edita la nota de forma
  // ADITIVA (conserva lo existente salvo que el médico pida lo contrario).
  const sendDoctorNote = useCallback(async (raw: string) => {
    const text = raw.trim();
    if (!text || asistenteBusy) return;
    setChatMessages(prev => [...prev, { role: 'user', text }]);
    setDoctorNoteInput('');
    setAsistenteBusy(true);
    try {
      // El código CIE-10 viaja DENTRO del texto —"Nombre (J00)"— porque es lo
      // único que permite al asistente devolverlo y que el plan lo clasifique
      // como diagnóstico en vez de adivinar por palabras clave.
      const diagnosticos = fhir
        .filter((f) => f.type === 'condition')
        .map((c) => (c.code ? `${c.display} (${c.code})` : c.display));
      // Medicamentos ESTRUCTURADOS: nombre solo el principio activo + campos
      // separados, para que el asistente refine sin duplicar la entrada.
      const medicamentos = fhir
        .filter((f) => f.type === 'medication')
        .map((m) => ({
          nombre: m.display,
          presentacion: m.presentation || '',
          dosis: m.dose || '',
          via: m.route || '',
          frecuencia: m.frequency || '',
          duracion: m.duration || '',
        }));
      const ORDER_TYPES = ['labOrder', 'labResult', 'imagingOrder', 'referral', 'procedure'];
      const ordenes = fhir.filter((f) => ORDER_TYPES.includes(f.type)).map((o) => o.display);
      const transcriptText = transcript
        .map((t) => `${t.speaker}: ${t.text}`)
        .join('\n');
      const historial = chatMessages
        .slice(-8)
        .map((m) => ({ rol: m.role === 'user' ? 'user' : 'ai', texto: m.text }));

      const fn = httpsCallable<
        Record<string, unknown>,
        {
          tipo: 'respuesta' | 'actualizacion' | 'rechazo';
          mensaje: string;
          soap: { s?: string; o?: string; a?: string; p?: string; e?: string } | null;
          diagnosticos: string[] | null;
          medicamentos: Array<{ nombre?: string; presentacion?: string; dosis?: string; via?: string; frecuencia?: string; duracion?: string }> | null;
          ordenes: string[] | null;
          educacion: string | null;
        }
      >(functions, 'portal_asistenteMedico');

      const res = await fn({
        consultaId,
        mensaje: text,
        paciente: {
          nombre: patient.nombre || patientContext.name,
          edad: patientContext.age || undefined,
          genero: patientContext.gender || undefined,
          alergias: patientContext.allergies,
          condiciones: patientContext.conditions,
          medicamentosPrevios: patientContext.medications,
        },
        motivo: patient.motivoConsulta || '',
        soap: localSoap,
        diagnosticos,
        medicamentos,
        ordenes,
        educacion: healthEducation || '',
        transcript: transcriptText,
        historial,
      });

      const d = res.data;
      setChatMessages(prev => [...prev, { role: 'ai', text: d.mensaje || 'Listo.' }]);

      if (d.tipo === 'actualizacion') {
        // NU: el asistente ya aplicó los cambios SERVER-SIDE en el documento
        // único (SOAP + categorías FHIR + educación); las tarjetas del plan
        // llegan por el snapshot. Aquí solo se adelanta el SOAP en la UI.
        if (d.soap) applySoap(d.soap);
        // Si el médico está en otra pestaña, marcar la nota con cambios sin leer.
        if (activeTab !== 'soap') setSoapHasUnread(true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo procesar la solicitud.';
      setChatMessages(prev => [...prev, { role: 'ai', text: `⚠️ ${msg}` }]);
    } finally {
      setAsistenteBusy(false);
    }
  }, [
    asistenteBusy, fhir, transcript, chatMessages, consultaId, patient.nombre,
    patient.motivoConsulta, patientContext, localSoap, healthEducation, applySoap,
    activeTab,
  ]);

  // ── Procesamiento de voz para el copiloto (voz → texto → copiloto) ──
  // Modo conversación: en cada pausa se procesa la frase y se sigue escuchando.
  // Si el copiloto está ocupado, las frases se encolan y se envían al liberarse
  // (manteniendo el contexto como una conversación continua).
  const voiceQueueRef = useRef<string[]>([]);
  const flushVoiceQueue = useCallback(() => {
    if (asistenteBusy || voiceQueueRef.current.length === 0) return;
    const batch = voiceQueueRef.current.join(' ').trim();
    voiceQueueRef.current = [];
    if (batch) sendDoctorNote(batch);
  }, [asistenteBusy, sendDoctorNote]);

  const copilotVoice = useSpeechToText({
    lang: 'es-PA',
    onInterim: (t) => setDoctorNoteInput(t),
    onSegment: (seg) => {
      setDoctorNoteInput('');              // la frase pasó a procesarse
      voiceQueueRef.current.push(seg);
      flushVoiceQueue();
    },
  });

  // Al terminar de procesar, enviar lo que se acumuló mientras tanto.
  useEffect(() => {
    if (!asistenteBusy) flushVoiceQueue();
  }, [asistenteBusy, flushVoiceQueue]);

  const handleSendDoctorNote = useCallback(() => {
    // Si está dictando, "enviar"/Enter detiene la escucha.
    if (copilotVoice.listening) { copilotVoice.stop(); return; }
    sendDoctorNote(doctorNoteInput);
  }, [copilotVoice, doctorNoteInput, sendDoctorNote]);

  // El input del copiloto crece con el texto (estilo Claude).
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = chatInputRef.current;
    if (!el || !copilotOpen) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [doctorNoteInput, copilotOpen]);

  // Auto-scroll del chat: cuando llega un mensaje nuevo (del médico o del
  // asistente) o aparece el indicador "pensando", baja al final para que
  // siempre se vean las últimas respuestas.
  const copilotFeedRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = copilotFeedRef.current;
    if (!el || !copilotOpen) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [chatMessages, asistenteBusy, copilotOpen]);

  /* ── Resumen para el paciente ─────────────────────────────────────────────
   * Firma del contenido clínico: cambia cuando cambia algo que el paciente
   * debe leer (el Plan, los recursos del plan con su posología, la educación).
   * Es lo que permite saber si el resumen sigue estando al día. */
  const firmaClinica = useMemo(
    () => [
      localSoap.p.trim(),
      healthEducation.trim(),
      ...fhir.map((f) => [f.type, f.display, f.presentation, f.dose, f.route,
                          f.frequency, f.duration, f.details].join(':')),
    ].join('|'),
    [localSoap.p, healthEducation, fhir],
  );
  const firmaClinicaRef = useRef(firmaClinica);
  firmaClinicaRef.current = firmaClinica;

  const generarResumenPaciente = useCallback(async ({ manual = false } = {}) => {
    if (resumenPacienteLoading) return;
    // Pedir "Regenerar" es una orden explícita: reabre el resumen a la IA
    // aunque el médico lo hubiera editado a mano antes.
    if (manual) resumenEditadoRef.current = false;
    setResumenPacienteLoading(true);
    setResumenPacienteError(null);
    const firmaAlPedir = firmaClinicaRef.current;
    try {
      const resumen = await fetchResumenPacienteTexto();
      if (resumen) {
        setResumenPaciente(resumen);
        resumenFirmaRef.current = firmaAlPedir;
      }
    } catch (err) {
      setResumenPacienteError(err instanceof Error ? err.message : 'No se pudo generar el resumen.');
    } finally {
      setResumenPacienteLoading(false);
    }
  }, [resumenPacienteLoading, fetchResumenPacienteTexto]);

  const generarResumenRef = useRef(generarResumenPaciente);
  generarResumenRef.current = generarResumenPaciente;

  /* Auto-generación. Dos correcciones sobre el comportamiento anterior:
   *
   * 1. Se esperaba solo a que el Plan tuviera texto — y ese es justo el
   *    instante en que los recursos aún no han llegado (el SOAP se aplica
   *    local al momento, `fhir` viaja por el snapshot). El resumen salía sin
   *    medicamentos ni exámenes.
   * 2. Se generaba UNA vez y se congelaba. Todo lo que el médico agregara
   *    después —un segundo fármaco, un laboratorio— no llegaba nunca al
   *    paciente.
   *
   * Ahora se espera a que el contenido se estabilice y se rehace cada vez que
   * cambia, salvo que el médico haya escrito el resumen a mano. */
  useEffect(() => {
    if (notaCerrada || activeTab !== 'soap' || resumenPacienteLoading) return;
    if (!localSoap.p.trim()) return;
    if (resumenEditadoRef.current) return;               // texto del médico: intocable
    if (resumenFirmaRef.current === firmaClinica) return; // ya está al día
    // La primera vez conviene que aparezca pronto; las regeneraciones esperan
    // más para no llamar a la IA en cada retoque del plan.
    const espera = resumenFirmaRef.current === null
      ? ESPERA_CONTENIDO_ESTABLE_MS
      : ESPERA_REGENERAR_RESUMEN_MS;
    const t = setTimeout(() => { void generarResumenRef.current(); }, espera);
    return () => clearTimeout(t);
  }, [notaCerrada, activeTab, resumenPacienteLoading, localSoap.p, firmaClinica]);

  /** El plan cambió después de que el médico escribiera el resumen a mano. */
  const resumenDesactualizado =
    !!resumenPaciente.trim() &&
    resumenEditadoRef.current &&
    resumenFirmaRef.current !== null &&
    resumenFirmaRef.current !== firmaClinica;

  /* ══════════ RENDER ══════════ */

  const consultaCargada = !!firestoreConsulta;
  const esSupervisor = role === 'admin' || role === 'supervisor';
  const accesoBloqueado =
    consultaCargada &&
    !esMedico &&
    !esSupervisor &&
    firestoreConsulta?.medicoId !== user?.uid;

  const consultaSinMedico =
    consultaCargada &&
    !esMedico &&
    esSupervisor &&
    firestoreConsulta?.estado === 'en_espera';

  if (accesoBloqueado) {
    return (
      <div className={styles.accessDenied}>
        <ShieldAlert size={48} aria-hidden="true" />
        <h2>Acceso restringido</h2>
        <p>No tiene permisos para ver esta consulta.</p>
        <button type="button" onClick={() => navigate(-1)}>Regresar</button>
      </div>
    );
  }

  return (
    <div className={`tcTokens ${styles.page} ${isFadingOut ? styles.fadeOut : ''}`}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {consultaSinMedico && (
        <div className={styles.supervisorWaitBanner} role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          <div>
            <strong>Modo supervisor — consulta en espera</strong>
            <p>
              Esta consulta aún no ha sido atendida por un médico. Sólo los usuarios
              con rol médico pueden iniciar consultas.
            </p>
          </div>
          <button type="button" onClick={() => navigate(-1)}>Volver a cola</button>
        </div>
      )}

      {/* ══ HEADER ══ */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={handleBack} type="button" title="Regresar" aria-label="Regresar">
            <ArrowLeft size={20} aria-hidden="true" />
          </button>
          {/* Foto de perfil (o iniciales) a la izquierda del nombre del paciente */}
          <Avatar className={styles.headerPatientAvatar} nombre={patient.nombre} fotoURL={patientFotoURL} zoomable />
          <div className={styles.headerPatient}>
            {/* La cédula va junto al nombre y en negrita: es lo que permite al
                médico distinguir a dos pacientes con nombres parecidos, y
                tenerla que ir a buscar a otra tarjeta durante la atención abre
                la puerta a documentar en el expediente equivocado. */}
            <h2 className={styles.headerPatientName}>
              <span className={styles.headerPatientNombre}>{patient.nombre || 'Cargando...'}</span>
              {cedulaPaciente && (
                <span className={styles.headerPatientCedula}>{cedulaPaciente}</span>
              )}
            </h2>
            <p className={styles.headerPatientMeta}>
              {patientContext.age > 0 ? `${patientContext.age} años` : 'Edad no registrada'}
              {patientContext.gender ? ` · ${patientContext.gender}` : ' · Sexo no registrado'}
              {/* Ubicación GPS capturada por la app al solicitar la consulta —
                  en una emergencia es adonde hay que despachar la ayuda. */}
              {firestoreConsulta?.pacienteUbicacion && (
                <>
                  {' · '}
                  <a
                    className={styles.headerUbicacion}
                    href={`https://www.google.com/maps?q=${firestoreConsulta.pacienteUbicacion.lat},${firestoreConsulta.pacienteUbicacion.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    title={firestoreConsulta.pacienteUbicacion.precisionM != null
                      ? `Posición al solicitar la consulta (precisión ±${firestoreConsulta.pacienteUbicacion.precisionM} m)`
                      : 'Posición al solicitar la consulta'}
                  >
                    <MapPin size={11} aria-hidden="true" /> Ubicación
                  </a>
                </>
              )}
            </p>
          </div>
        </div>
        <div className={styles.headerRight}>
          {/* Call controls — el dock inferior sobre el video es el control
              principal; estos controles del header quedan SOLO como respaldo
              cuando el panel de video está oculto (para que Colgar/Mic/Cam
              nunca queden inaccesibles). */}
          {videoStarted && !videoDisabled && !videoOpen && (
            <>
              <div className={styles.headerControls} role="toolbar" aria-label="Controles de videollamada">
                <button className={`${styles.headerCtrlBtn} ${!isMicOn ? styles.headerCtrlOff : ''}`} onClick={() => setIsMicOn(p => !p)} title="Micrófono (M)" type="button">
                  {isMicOn ? <Mic size={16} aria-hidden="true" /> : <MicOff size={16} aria-hidden="true" />}
                </button>
                <button className={`${styles.headerCtrlBtn} ${!isCameraOn ? styles.headerCtrlOff : ''}`} onClick={alternarCamara} title="Cámara (V)" type="button">
                  {isCameraOn ? <Video size={16} aria-hidden="true" /> : <VideoOff size={16} aria-hidden="true" />}
                </button>
                <button
                  className={`${styles.headerCtrlBtn} ${!transcriptionEnabled ? styles.headerCtrlOff : ''}`}
                  onClick={handleToggleTranscripcion}
                  title={transcriptionEnabled
                    ? 'Detener transcripción (T): dejará de generarse la nota médica y no se guardará la conversación'
                    : 'Reactivar transcripción (T)'}
                  aria-pressed={!transcriptionEnabled}
                  type="button"
                >
                  {transcriptionEnabled ? <Captions size={16} aria-hidden="true" /> : <CaptionsOff size={16} aria-hidden="true" />}
                </button>
                {/* M8 — "Colgar" = PRIMER corte: termina la videollamada con el
                    paciente (lo libera y detiene la facturación), pero el médico
                    PERMANECE en la pantalla para editar la nota y finalizar la
                    atención después (segundo corte). */}
                <button
                  onClick={handleTerminarLlamada}
                  disabled={isEndingCall}
                  title="Colgar: terminar la llamada con el paciente. Podrá editar la nota y finalizar la atención después."
                  type="button"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#c0392b', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: isEndingCall ? 'default' : 'pointer', opacity: isEndingCall ? 0.6 : 1 }}
                >
                  <PhoneOff size={15} aria-hidden="true" /> {isEndingCall ? 'Terminando…' : 'Colgar'}
                </button>
              </div>

              {transcriptionEnabled && <AiStatusPill status={aiStatus} />}
            </>
          )}

          {/* Modo revisión: guardar solo si la consulta NO está cerrada. Una
              consulta firmada no admite cambios, así que ofrecer «Guardar»
              sería prometer algo que el backend rechaza. */}
          {modoRevision && !notaCerrada && (
            <Button variant="primary" onClick={handleGuardarRevision} loading={guardandoRevision}>
              <Save size={14} aria-hidden="true" /> Guardar cambios y volver al inicio
            </Button>
          )}

          {/* Finalize button — shown when video is disabled (primer corte M8 o sesión expirada).
              Se BLOQUEA mientras la IA aún está generando la nota, para no finalizar
              con la nota a medias; se muestra el estado de procesamiento. */}
          {videoDisabled && !modoRevision && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              {(isProcessing || hasPendingBuffer) && <AiStatusPill status={aiStatus} />}
              <Button
                variant="primary"
                onClick={() => setShowEndCallModal(true)}
                disabled={isProcessing || hasPendingBuffer}
                title={
                  isProcessing || hasPendingBuffer
                    ? 'Espere: se está generando la nota médica con IA'
                    : undefined
                }
              >
                <PhoneOff size={14} aria-hidden="true" />{' '}
                {isProcessing || hasPendingBuffer
                  ? 'Generando nota…'
                  : 'Finalizar atención completa'}
              </Button>
            </div>
          )}

          {videoStarted && (
            sessionTimer.endsAt !== null ? (
              <div className={styles.deadlineWrap}>
                <button
                  type="button"
                  className={`${styles.deadlineBadge} ${styles[`deadline_${sessionTimer.warningLevel}`] ?? ''}`}
                  onClick={() => setShowExtendMenu((v) => !v)}
                  title={
                    sessionTimer.warningLevel === 'expired'
                      ? 'La sesión expiró'
                      : `Tiempo restante. ${sessionTimer.extensionesRestantes} extensiones disponibles.`
                  }
                  aria-label={`Tiempo restante ${formatRemaining(sessionTimer.remainingSec)}. Click para extender.`}
                  disabled={sessionTimer.warningLevel === 'expired' || sessionTimer.extensionesRestantes === 0}
                >
                  <TimerReset size={12} aria-hidden="true" />
                  <span className={styles.deadlineValue}>{formatRemaining(sessionTimer.remainingSec)}</span>
                </button>
                <span className={styles.timerSubBadge}>{formatDuration(callDuration)}</span>

                {showExtendMenu && (
                  <div className={styles.extendMenu} role="menu">
                    <div className={styles.extendMenuHeader}>
                      <span>Extender sesión</span>
                      <button type="button" className={styles.extendMenuClose} onClick={() => setShowExtendMenu(false)} aria-label="Cerrar menú">
                        <X size={12} aria-hidden="true" />
                      </button>
                    </div>
                    <div className={styles.extendMenuOptions}>
                      {([5, 10, 15] as const).map((m) => (
                        <button key={m} type="button" className={styles.extendOption} onClick={() => handleExtenderSesion(m)} disabled={extendBusy || sessionTimer.extensionesRestantes === 0}>
                          <Plus size={11} aria-hidden="true" /> {m} min
                        </button>
                      ))}
                    </div>
                    <p className={styles.extendMenuInfo}>{sessionTimer.extensionesRestantes} de 3 extensiones disponibles</p>
                    {extendError && <p className={styles.extendMenuError}>{extendError}</p>}
                  </div>
                )}
              </div>
            ) : (
              <span className={styles.timerMono}>{formatDuration(callDuration)}</span>
            )
          )}

          <button className={styles.headerIcon} title="Modo oscuro" type="button"><Moon size={16} aria-hidden="true" /></button>
          <button className={styles.headerIcon} title="Notificaciones" type="button"><Bell size={16} aria-hidden="true" /></button>

          <div className={styles.doctorChip}>
            <Avatar className={styles.doctorChipAvatar} nombre={doctorName} fotoURL={doctorFotoURL} />
            <span className={styles.doctorChipName}>{doctorName}</span>
          </div>
        </div>
      </header>

      {/* ══ BANNER: TRANSCRIPCIÓN DETENIDA ══
          Muy visible (ancho completo, bajo el header): mientras esté detenida
          NO se generan notas médicas ni se guarda la conversación. Incluye el
          botón para reactivarla. */}
      {videoStarted && !videoDisabled && !modoRevision && !transcriptionEnabled && (
        <div className={styles.transcriptStoppedBanner} role="alert">
          <CaptionsOff size={20} aria-hidden="true" className={styles.transcriptStoppedIcon} />
          <span className={styles.transcriptStoppedText}>
            <strong>Transcripción detenida.</strong>{' '}
            No se están generando notas médicas ni se guardará la conversación
            mientras la transcripción permanezca detenida.
          </span>
          <button
            type="button"
            className={styles.transcriptStoppedBtn}
            onClick={handleToggleTranscripcion}
          >
            <Captions size={15} aria-hidden="true" /> Reactivar transcripción
          </button>
        </div>
      )}

      {/* ══ BANNER: CITA EN HCIS NO CREADA ══
          La cita en HCIS falló. El médico puede reintentar o continuar la
          consulta igualmente (no bloquea). */}
      {videoStarted && !modoRevision && citaSeisError && (
        <div className={styles.citaSeisBanner} role="alert">
          <AlertTriangle size={20} aria-hidden="true" className={styles.citaSeisIcon} />
          <span className={styles.citaSeisText}>
            <strong>La cita no se creó en HCIS.</strong>{' '}
            {citaSeisError} Puede reintentar o continuar con la consulta.
          </span>
          <div className={styles.citaSeisActions}>
            <button
              type="button"
              className={styles.citaSeisRetryBtn}
              onClick={enviarCitaSeis}
              disabled={citaSeisEnviando}
            >
              <TimerReset size={15} aria-hidden="true" />
              {citaSeisEnviando ? 'Reintentando…' : 'Reintentar'}
            </button>
            <button
              type="button"
              className={styles.citaSeisDismissBtn}
              onClick={() => setCitaSeisError(null)}
              title="Continuar sin crear la cita"
            >
              Continuar
            </button>
          </div>
        </div>
      )}

      {/* ══ BODY ══ */}
      <div className={styles.body}>

        {/* Botón para mostrar el panel de video cuando está oculto (borde izq.) */}
        {!videoOpen && (
          <button
            type="button"
            className={styles.videoExpandBtn}
            onClick={() => setVideoOpen(true)}
            title="Mostrar panel de video"
            aria-label="Mostrar panel de video"
          >
            <PanelLeftOpen size={16} aria-hidden="true" />
          </button>
        )}

        {/* ── COL MAIN ── */}
        <div className={styles.colMain} style={{ display: videoOpen ? undefined : 'none' }}>

          {/* Botón para ocultar el panel — a la derecha del panel de video */}
          <button
            type="button"
            className={styles.videoCollapseBtn}
            onClick={() => setVideoOpen(false)}
            title="Ocultar panel de video"
            aria-label="Ocultar panel de video"
          >
            <PanelLeftClose size={16} aria-hidden="true" />
          </button>

          {modoRevision ? (
            /* ── Modo revisión (desde historial): video deshabilitado ── */
            <div className={styles.videoArea}>
              <div className={styles.videoDisabledState}>
                <PhoneOff size={48} className={styles.videoDisabledIcon} aria-hidden="true" />
                <h3 className={styles.videoDisabledTitle}>Videollamada deshabilitada</h3>
                <p className={styles.videoDisabledText}>
                  {notaCerrada
                    ? 'Esta consulta ya fue finalizada: la nota está firmada y es de solo lectura. Puede consultarla y copiarla, pero no modificarla. Para dejar constancia de algo nuevo, agregue una nota de observación desde el detalle de la consulta.'
                    : 'Está revisando una consulta desde el historial. La videollamada está deshabilitada. Puede editar la nota médica y revisar la información del paciente; cuando termine, use «Guardar cambios y volver al inicio». Los cambios quedan registrados en la bitácora.'}
                </p>
              </div>
            </div>
          ) : !consultaCargada ? (
            /* ── Cargando la consulta: evita el parpadeo del panel equivocado
               (p. ej. mostrar «Iniciar videollamada» antes de saber que el
               médico ya colgó). ── */
            <div className={styles.videoArea}>
              <div className={styles.videoDisabledState}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} aria-hidden="true" />
                <p className={styles.videoDisabledText}>Cargando la consulta…</p>
              </div>
            </div>
          ) : (!videoStarted && !videoDisabled) ? (
            /* ── Pre-consulta: revision de paciente antes de iniciar video.
               Solo si NO está deshabilitado (si el médico ya colgó, se muestra
               el estado «finalizada» de abajo, no este panel). ── */
            <div className={styles.preConsultaPanel}>
              <div className={styles.preConsultaContent}>
                <div className={styles.preConsultaHeader}>
                  <Avatar className={styles.preConsultaAvatar} nombre={patient.nombre} fotoURL={patientFotoURL} zoomable />
                  <div>
                    <h2 className={styles.preConsultaName}>{patient.nombre || 'Cargando...'}</h2>
                    <p className={styles.preConsultaMeta}>
                      {patientContext.age > 0 ? `${patientContext.age} anos` : 'Edad no registrada'}
                      {patientContext.gender ? ` · ${patientContext.gender}` : ''}
                      {cedulaPaciente ? ` · ${cedulaPaciente}` : ''}
                    </p>
                  </div>
                </div>

                <div className={styles.preConsultaMotivo}>
                  <div className={styles.motivoCalloutIcon}><ClipboardList size={18} aria-hidden="true" /></div>
                  <div>
                    <p className={styles.motivoLabel}>MOTIVO DE LA CONSULTA</p>
                    <p className={styles.motivoText}>{patient.motivoConsulta || <em className={styles.soapEmpty}>Sin motivo registrado</em>}</p>
                  </div>
                </div>

                {patientContext.allergies.length > 0 && (
                  <div className={styles.preConsultaSection}>
                    <h5 className={styles.infoSectionTitle}><ShieldAlert size={12} /> Alergias</h5>
                    <div className={styles.tagGroup}>{patientContext.allergies.map((a, i) => <span key={i} className={`${styles.tag} ${styles.tagAllergy}`}>{a}</span>)}</div>
                  </div>
                )}

                {patientContext.medications.length > 0 && (
                  <div className={styles.preConsultaSection}>
                    <h5 className={styles.infoSectionTitle}><Pill size={12} /> Medicacion actual</h5>
                    <div className={styles.tagGroup}>{patientContext.medications.map((m, i) => <span key={i} className={`${styles.tag} ${styles.tagMedication}`}>{m}</span>)}</div>
                  </div>
                )}

                {patientContext.conditions.length > 0 && (
                  <div className={styles.preConsultaSection}>
                    <h5 className={styles.infoSectionTitle}><Stethoscope size={12} /> Condiciones</h5>
                    <div className={styles.tagGroup}>{patientContext.conditions.map((c, i) => <span key={i} className={`${styles.tag} ${styles.tagCondition}`}>{c}</span>)}</div>
                  </div>
                )}

                {/* Aviso pre-video: el médico tiene tiempo limitado para
                    iniciar la videollamada; si no, el paciente vuelve a la cola. */}
                {/* Diseño en dos líneas FIJAS (etiqueta + contador grande a la
                    derecha, aclaración debajo): en monitores de poca
                    resolución la frase corrida anterior se quebraba en 2-3
                    líneas alrededor del contador y se leía mal. */}
                {preVideoRemainingSec != null && (
                  <div
                    role="status"
                    style={{
                      margin: '4px 0 12px', padding: '10px 14px', borderRadius: 10,
                      background: preVideoRemainingSec <= 60 ? 'rgba(183,28,28,0.08)' : 'rgba(180,120,10,0.08)',
                      color: preVideoRemainingSec <= 60 ? 'var(--tc-error, #b71c1c)' : 'var(--tc-warn, #8a6d1f)',
                      border: `1px solid ${preVideoRemainingSec <= 60 ? 'rgba(183,28,28,0.25)' : 'rgba(180,120,10,0.25)'}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <AlertTriangle size={15} aria-hidden="true" style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, lineHeight: 1.25 }}>
                        {preVideoRemainingSec > 0 ? 'Tiempo para iniciar la videollamada' : 'Tiempo agotado'}
                      </span>
                      {preVideoRemainingSec > 0 && (
                        <strong style={{ fontSize: 21, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', flexShrink: 0 }}>
                          {formatRemaining(preVideoRemainingSec)}
                        </strong>
                      )}
                    </div>
                    <p style={{ margin: '4px 0 0 23px', fontSize: 11.5, fontWeight: 500, lineHeight: 1.35, opacity: 0.85 }}>
                      {preVideoRemainingSec > 0
                        ? 'Si no la inicia, el paciente volverá a la cola automáticamente.'
                        : 'Devolviendo al paciente a la cola…'}
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  className={styles.startVideoBtn}
                  onClick={() => setShowConsent(true)}
                >
                  <Video size={20} aria-hidden="true" />
                  Iniciar videollamada
                </button>

                {/* Atender por teléfono — cuando el video no logra establecerse.
                    Sin esta salida el médico quedaba atrapado: solo podía
                    iniciar el video o devolver al paciente, y el contador
                    pre-video lo re-encolaba en plena llamada telefónica. */}
                <button
                  type="button"
                  onClick={handleAtenderSinVideo}
                  disabled={pasandoATelefonica}
                  title="Continuar la atención por llamada telefónica y luego finalizar normalmente"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    marginTop: 10, padding: '10px 16px', width: '100%',
                    background: 'transparent', color: 'var(--tc-accent-700, #0f766e)',
                    border: '1px solid var(--tc-accent-600, #0d9488)', borderRadius: 10,
                    fontSize: 14, fontWeight: 600,
                    cursor: pasandoATelefonica ? 'default' : 'pointer', opacity: pasandoATelefonica ? 0.6 : 1,
                  }}
                >
                  <Phone size={16} aria-hidden="true" />
                  {pasandoATelefonica ? 'Cambiando…' : 'Atender por llamada telefónica'}
                </button>

                {/* Devolver a la cola — solo disponible antes de iniciar el video */}
                <button
                  type="button"
                  onClick={handleDevolverACola}
                  disabled={devolviendo}
                  title="Devolver el paciente a la cola para que otro médico lo tome"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    marginTop: 10, padding: '10px 16px', width: '100%',
                    background: 'transparent', color: 'var(--tc-text-2, #475569)',
                    border: '1px solid var(--tc-line, #d8dee4)', borderRadius: 10,
                    fontSize: 14, fontWeight: 600,
                    cursor: devolviendo ? 'default' : 'pointer', opacity: devolviendo ? 0.6 : 1,
                  }}
                >
                  <CornerUpLeft size={16} aria-hidden="true" />
                  {devolviendo ? 'Devolviendo…' : 'Devolver a la cola'}
                </button>
              </div>
            </div>
          ) : videoDisabled ? (
            /* ── Post-expiry: video disabled, doctor can still edit notes ── */
            <div className={styles.videoArea}>
              <div className={styles.videoDisabledState}>
                {atencionTelefonica
                  ? <Phone size={48} className={styles.videoDisabledIcon} aria-hidden="true" />
                  : <PhoneOff size={48} className={styles.videoDisabledIcon} aria-hidden="true" />}
                <h3 className={styles.videoDisabledTitle}>
                  {atencionTelefonica ? 'Atención por llamada telefónica' : 'Videollamada finalizada'}
                </h3>
                <p className={styles.videoDisabledText}>
                  {atencionTelefonica
                    ? 'Esta consulta se está atendiendo por teléfono. El paciente ya no volverá a la cola. Escriba la nota médica, use el copiloto y, cuando termine, finalice la atención.'
                    : 'La videollamada con el paciente finalizó. Puede continuar editando la nota médica, usar el copiloto y revisar la información del paciente. Cuando termine, finalice la atención completa.'}
                </p>
                <Button
                  variant="primary"
                  onClick={() => setShowEndCallModal(true)}
                  disabled={isProcessing || hasPendingBuffer}
                  title={
                    isProcessing || hasPendingBuffer
                      ? 'Espere: se está generando la nota médica con IA'
                      : undefined
                  }
                >
                  <PhoneOff size={14} aria-hidden="true" />{' '}
                  {isProcessing || hasPendingBuffer ? 'Generando nota…' : 'Finalizar atención completa'}
                </Button>

                {/* Finalizar no puede ser la única salida de la atención
                    telefónica: el video puede restablecerse, o el médico puede
                    no lograr comunicarse y necesitar devolver al paciente. */}
                {atencionTelefonica && (
                  <div className={styles.accionesTelefonica}>
                    <button
                      type="button"
                      onClick={handleVolverAVideo}
                      disabled={pasandoATelefonica}
                      title="Reintentar la videollamada con el paciente"
                    >
                      <Video size={14} aria-hidden="true" />
                      {pasandoATelefonica ? 'Volviendo…' : 'Volver a la videollamada'}
                    </button>
                    <button
                      type="button"
                      onClick={handleDevolverACola}
                      disabled={devolviendo}
                      title="Devolver el paciente a la cola para que otro médico lo tome"
                    >
                      <CornerUpLeft size={14} aria-hidden="true" />
                      {devolviendo ? 'Devolviendo…' : 'Devolver a la cola'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ── Video area — two adaptive participant cards side by side ── */
            <div className={styles.videoArea}>
              {(otraVentanaTieneLaSala || expulsadoPorDuplicado) ? (
                /* Ni se intenta conectar: hacerlo expulsaría a la otra ventana y se entraría
                   en un rebote de expulsiones mutuas, que es peor que no conectar. */
                <div className={styles.avatarPlaceholder} role="alert">
                  <div className={styles.audioFirstWrap}>
                    <MonitorX size={44} aria-hidden="true" style={{ color: '#f5b544' }} />
                    <p className={styles.audioFirstName}>Esta consulta está abierta en otra ventana</p>
                    <span className={`${styles.audioFirstPill} ${styles.audioFirstPillMuted}`}>
                      {expulsadoPorDuplicado
                        ? 'Otra ventana tomó la videollamada, por eso esta se desconectó.'
                        : 'Para evitar que se corte la llamada, el video solo se abre en una ventana.'}
                    </span>
                    <button
                      type="button"
                      onClick={() => { setExpulsadoPorDuplicado(false); tomarControl(); }}
                      style={{
                        marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '10px 20px', borderRadius: 10, border: 'none',
                        background: '#1A8048', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      <RefreshCw size={16} aria-hidden="true" />
                      Usar esta ventana
                    </button>
                  </div>
                </div>
              ) : livekitReady ? (
                <LiveKitRoom key={`lk-${reconnectNonce}`} serverUrl={livekitUrl!} token={livekitToken!} connect video={isCameraOn} audio={isMicOn}
                  options={OPCIONES_SALA} connectOptions={opcsConexion}
                  onDisconnected={handleLivekitDisconnected} onError={err => console.error('LiveKit:', err)}
                  style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                  <ConnectionBanner />
                  <AutoReconnectWatchdog onForceRejoin={handleAutoRejoin} onStable={handleConnectionStable} />
                  <ReconnectionOverlay onRetry={handleManualReconnect} motivo={avisoConexion} nivelAviso={nivelAvisoConexion} />
                  {/* Escalera de avisos de conexión: silencio → banner discreto → instrucción. */}
                  <AvisosConexion consultaId={consultaId} medioPropioCaido={cameraError} onNivelChange={setNivelAvisoConexion} />
                  {/* SUP-01: aviso al médico de que un supervisor está observando. */}
                  <SupervisorIndicator />
                  {/* El paciente colgó / cerró la app: banner con "Volver a llamar".
                      Debe vivir DENTRO de <LiveKitRoom> (usa useRemoteParticipants). */}
                  {esMedico && !modoRevision && consultaId && (
                    <PacienteColgoBanner consultaId={consultaId} consulta={firestoreConsulta} onColgar={handleTerminarLlamada} colgando={isEndingCall} />
                  )}
                  {/* REC-01: indicador de grabación siempre visible durante la llamada. */}
                  <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 20 }}>
                    <IndicadorGrabacion active={grabacionActiva} variant="badge" />
                  </div>
                  <MediaSync micEnabled={isMicOn} camEnabled={isCameraOn} consultaId={consultaId} reconnectNonce={reconnectNonce}
                    onPublicacionPerdida={handleAutoRejoin} onCamaraSinPublicar={setCameraError} />

                  <div className={styles.participantsRow}>
                    {/* Patient card — aspect ratio adapts to source */}
                    <div className={styles.participantCard} style={{ aspectRatio: patientAspect }}>
                      <PatientVideo patientName={patient.nombre} patientFotoURL={patientFotoURL} onAspectChange={setPatientAspect} onMicMutedChange={setPatientMicMuted} onSinSenalChange={handlePacienteSinSenal} avisoLlamada={firestoreConsulta?.avisoLlamada} />
                      <div className={styles.tileLabel}>
                        <span className={styles.liveDot} /> {patient.nombre || 'Paciente'}
                        {patientMicMuted && (
                          <span className={styles.micMutedTag} title="Micrófono del paciente silenciado" aria-label="Micrófono del paciente silenciado">
                            <MicOff size={12} aria-hidden="true" />
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Doctor card — landscape webcam */}
                    <div className={styles.participantCard} style={{ aspectRatio: 16 / 9 }}>
                      <DoctorLocalVideo doctorName={doctorName} doctorFotoURL={doctorFotoURL} cameraOn={isCameraOn} cameraError={cameraError} />
                      <div className={styles.tileLabel}>Tu</div>
                    </div>
                  </div>

                  <VirtualBgApplier selected={selectedBackground} onLoadingChange={setBgLoading} onError={setBgError} />
                  {/* AgentDetector removido — dispatch explícito del agente */}
                  <TranscriptionCollector enabled={transcriptionEnabled} onSegment={addSegment} onInterim={setInterim} onListeningChange={setTranscriptionListening} />
                  {/* BrowserSpeechBridge deshabilitado — toda la transcripción
                      viene del agente LiveKit server-side */}
                </LiveKitRoom>
              ) : (
                /* ── Placeholder (connecting to LiveKit) ── */
                <div className={styles.participantsRow}>
                  <div className={styles.participantCard} style={{ aspectRatio: patientAspect }}>
                    <div className={styles.avatarPlaceholder}>
                      <Avatar className={styles.avatarCircleLg} nombre={patient.nombre} fotoURL={patientFotoURL} zoomable />
                      {/* Si el token falló, DECIRLO. Antes esta rama pintaba el
                          avatar y nada más: fuera de horario, con el servidor
                          caído o sin credenciales, el médico veía un
                          "Conectando…" perpetuo sin una sola pista del motivo.
                          El mensaje viene del backend y ya explica el caso. */}
                      {livekitError && (
                        <div className={styles.motivoSinVideo} role="alert">
                          <p className={styles.motivoSinVideoTitulo}>No se pudo iniciar la videollamada</p>
                          <p className={styles.motivoSinVideoTexto}>{livekitError}</p>
                          <button type="button" className={styles.motivoSinVideoBoton} onClick={handleManualReconnect}>
                            Reintentar
                          </button>
                        </div>
                      )}
                    </div>
                    <div className={styles.tileLabel}><span className={styles.liveDot} /> {patient.nombre || 'Paciente'}</div>
                  </div>
                  <div className={styles.participantCard} style={{ aspectRatio: 16 / 9 }}>
                    <div className={styles.avatarPlaceholder}>
                      <Avatar className={styles.avatarCircleLg} nombre={doctorName} fotoURL={doctorFotoURL} />
                    </div>
                    <div className={styles.tileLabel}>Tu</div>
                  </div>
                </div>
              )}

              {/* ── Dock de controles — inferior centrado (estándar) ──
                  Micrófono, Cámara, Fondo virtual, separador y Colgar (primer
                  corte M8). Los atajos de teclado existentes se conservan. */}
              <div className={styles.controlDock} role="toolbar" aria-label="Controles de videollamada">
                <button
                  type="button"
                  className={`${styles.dockBtn} ${!isMicOn ? styles.dockOff : ''}`}
                  onClick={() => setIsMicOn(p => !p)}
                  title="Micrófono (M)"
                  aria-label="Micrófono (M)"
                >
                  {isMicOn ? <Mic size={19} aria-hidden="true" /> : <MicOff size={19} aria-hidden="true" />}
                </button>
                <button
                  type="button"
                  className={`${styles.dockBtn} ${!isCameraOn ? styles.dockOff : ''}`}
                  onClick={alternarCamara}
                  title="Cámara (V)"
                  aria-label="Cámara (V)"
                >
                  {isCameraOn ? <Video size={19} aria-hidden="true" /> : <VideoOff size={19} aria-hidden="true" />}
                </button>
                <button
                  type="button"
                  className={`${styles.dockBtn} ${showBgPicker ? styles.dockAccent : ''}`}
                  onClick={() => {
                    setStagedBackground(selectedBackground);
                    setShowBgPicker(true);
                  }}
                  title="Fondo virtual"
                  aria-label="Fondo virtual"
                >
                  <Image size={19} aria-hidden="true" />
                </button>
                <div className={styles.dockSep} aria-hidden="true" />
                {/* M8 — "Colgar" = PRIMER corte: termina la videollamada con el
                    paciente, pero el médico permanece para editar la nota y
                    finalizar la atención después (segundo corte). */}
                <button
                  type="button"
                  className={styles.dockHangUp}
                  onClick={handleTerminarLlamada}
                  disabled={isEndingCall}
                  title="Colgar llamada"
                  aria-label="Colgar llamada"
                >
                  <PhoneOff size={19} aria-hidden="true" />
                </button>
              </div>

              {/* ── Selector de fondo virtual ────────────────────────────
                  Panel compacto al estilo Teams: mosaicos pequeños agrupados
                  (efectos primero, imágenes después) y aplicación INMEDIATA al
                  hacer clic. El diseño anterior usaba tarjetas 16:9 en tres
                  columnas con etiqueta debajo y un pie de "Aplicar": ocupaba
                  tanto que en monitores pequeños el panel desbordaba su propio
                  contenedor y aparecían barras de scroll en ambos ejes.
                  Las medidas ahora son relativas al área de video (%), no al
                  viewport, así que el panel nunca puede ser más grande que el
                  espacio donde vive. */}
              {showBgPicker && (
                <div
                  className={styles.bgModalOverlay}
                  role="dialog"
                  aria-modal="true"
                  aria-label="Fondo virtual"
                  onClick={(e) => { if (e.target === e.currentTarget) setShowBgPicker(false); }}
                >
                  <div className={styles.bgModal}>
                    <div className={styles.bgModalHeader}>
                      <div className={styles.bgModalTitleContainer}>
                        <h3 className={styles.bgModalTitle}>Fondo virtual</h3>
                        <p className={styles.bgModalSubtitle}>
                          {bgLoading
                            ? 'Aplicando…'
                            : `${(stagedBackground || selectedBackground).label} · se recuerda para sus próximas llamadas`}
                        </p>
                      </div>
                      <button
                        className={styles.bgModalClose}
                        onClick={() => setShowBgPicker(false)}
                        type="button"
                        aria-label="Cerrar"
                      >
                        <X size={16} aria-hidden="true" />
                      </button>
                    </div>

                    <div className={styles.bgModalContent}>
                      {([
                        { titulo: 'Sin fondo y desenfoque', items: allBackgrounds.filter((b) => b.kind !== 'image') },
                        { titulo: 'Imágenes', items: allBackgrounds.filter((b) => b.kind === 'image') },
                      ] as const).map(({ titulo, items }) => items.length > 0 && (
                        <div key={titulo} className={styles.bgGrupo}>
                          <span className={styles.bgGrupoTitulo}>{titulo}</span>
                          <div className={styles.bgGrid}>
                            {items.map((bg: BackgroundOption) => {
                              const activo = (stagedBackground || selectedBackground).id === bg.id;
                              return (
                                <button
                                  key={bg.id}
                                  type="button"
                                  className={`${styles.bgTile} ${activo ? styles.bgTileActivo : ''}`}
                                  style={{ background: bg.preview }}
                                  onClick={() => aplicarFondo(bg)}
                                  disabled={bgLoading}
                                  title={bg.label}
                                  aria-label={bg.label}
                                  aria-pressed={activo}
                                >
                                  {bg.kind === 'none' && <Ban size={15} aria-hidden="true" />}
                                  {bg.kind === 'blur' && <Aperture size={15} aria-hidden="true" />}
                                  <span className={styles.bgTileLabel}>{bg.label}</span>
                                  {activo && (
                                    <span className={styles.bgTileCheck} aria-hidden="true">
                                      <Check size={11} />
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}

                      {bgError && (
                        <div className={styles.bgModalError}>
                          <AlertTriangle size={14} aria-hidden="true" />
                          <span>{bgError}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Caption bar: TRANSCRIPCIÓN EN VIVO persistente bajo el video ──
              Misma fuente de datos y mismo diseño de conversación que la
              pestaña "Transcripción". Oculta en pre-llamada y en modo revisión. */}
          {videoStarted && !modoRevision && (
            <div className={`${styles.captionBar} ${captionOpen ? styles.captionBarOpen : ''}`}>
              <div className={styles.captionHeader}>
                <span className={styles.captionTitle}>Transcripción en vivo</span>
                {transcriptionEnabled && !videoDisabled && <AiStatusPill status={aiStatus} />}
                <button
                  type="button"
                  className={styles.captionToggle}
                  onClick={toggleCaption}
                  title={captionOpen ? 'Contraer transcripción' : 'Expandir transcripción'}
                  aria-expanded={captionOpen}
                >
                  {captionOpen
                    ? <>Contraer <ChevronUp size={13} aria-hidden="true" /></>
                    : <>Expandir <ChevronDown size={13} aria-hidden="true" /></>}
                </button>
              </div>
              <div className={styles.captionBody} ref={captionBodyRef}>
                {transcript.length === 0 && !interimText ? (
                  <p className={styles.txWaiting}>Esperando conversación…</p>
                ) : (
                  <TranscriptThread
                    transcript={transcript}
                    interimText={interimText}
                    interimSpeaker={interimSpeaker}
                    doctorName={doctorName}
                    patientName={patient.nombre}
                  />
                )}
              </div>
            </div>
          )}

        </div>

        {/* Horizontal resize handle (col-main ↔ col-side) — solo con video visible */}
        {videoOpen && (
          <div className={styles.resizeHandleV} onMouseDown={handleHResizeDown} />
        )}

        {/* ── COL SIDE — panel de pestañas. Si el video está oculto, crece para
            ocupar el espacio libre. ── */}
        <div
          className={styles.colSide}
          style={videoOpen ? { width: sideWidth } : { flex: 1, minWidth: 0 }}
        >
          {/* M11 — Alerta de reconsulta: paciente atendido en las últimas 48h */}
          {reconsulta48h && (
            <div
              role="alert"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 14px', background: '#fdf6e3', color: '#8a6d1f',
                borderBottom: '1px solid #ecd9a0', fontSize: 13, fontWeight: 600,
              }}
            >
              <AlertTriangle size={15} aria-hidden="true" />
              Este paciente fue atendido en las últimas 48 horas.
            </div>
          )}
          {/* Tab navigation */}
          <nav className={styles.sideTabs}>
            {([
              { id: 'soap' as const, label: 'Nota médica', icon: <ClipboardList size={14} /> },
              { id: 'patient' as const, label: 'Paciente', icon: <User size={14} /> },
              { id: 'expediente' as const, label: 'Expediente', icon: <BookOpen size={14} /> },
              { id: 'transcript' as const, label: 'Transcripción', icon: <MessageSquareText size={14} /> },
            ] as const).map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`${styles.sideTab} ${activeTab === tab.id ? styles.sideTabActive : ''}`}
                onClick={() => { setActiveTab(tab.id); if (tab.id === 'soap') setSoapHasUnread(false); }}
              >
                {tab.icon} {tab.label}
                {tab.id === 'soap' && soapHasUnread && <span className={styles.unreadDot} />}
                {tab.id === 'transcript' && transcript.length > 0 && <span className={styles.tabBadge}>{transcript.length}</span>}
              </button>
            ))}
          </nav>

          {/* Tab content */}
          <div className={styles.sideContent}>

            {/* ── TAB: Nota médica (SOAP + Resumen) ── */}
            {activeTab === 'soap' && (
              <div className={styles.soapTab}>
                <div className={styles.soapHead}>
                  <span className={`${styles.soapStatus} ${isProcessing ? styles.soapStatusProcessing : hasSOAPContent ? styles.soapStatusReady : ''}`}>
                    <span className={styles.soapStatusDot} />
                    {isProcessing ? 'Procesando transcripción...' : hasSOAPContent ? 'Nota generada' : 'Esperando datos'}
                  </span>
                  {hasPendingUpdates && (
                    <span className={styles.pendingBadge}>IA tiene actualizaciones</span>
                  )}
                  {hasSOAPContent && (
                    <button
                      type="button"
                      className={`${styles.copiarSoape} ${soapeCopiado ? styles.copiarSoapeHecho : ''}`}
                      onClick={handleCopiarSoape}
                      title="Copiar la nota SOAPE completa al portapapeles"
                      aria-label="Copiar la nota SOAPE completa al portapapeles"
                    >
                      {soapeCopiado
                        ? <><Check size={12} aria-hidden="true" /> Copiado</>
                        : <><Copy size={12} aria-hidden="true" /> Copiar SOAPE</>}
                    </button>
                  )}
                  {hasSOAPContent && (
                    <button
                      type="button"
                      className={`${styles.copiarSoape} ${seleccionEstado === 'copiado' ? styles.copiarSoapeHecho : ''}`}
                      onClick={handleCopiarSeleccion}
                      title="Copiar solo el texto sombreado de una sección"
                      aria-label="Copiar solo el texto sombreado de una sección"
                    >
                      {seleccionEstado === 'copiado'
                        ? <><Check size={12} aria-hidden="true" /> Copiado</>
                        : seleccionEstado === 'vacio'
                          ? <><TextSelect size={12} aria-hidden="true" /> Sombree texto primero</>
                          : <><TextSelect size={12} aria-hidden="true" /> Copiar selección</>}
                    </button>
                  )}
                </div>

                {aiError && <div className={styles.aiError}>{aiError}</div>}

                {allAlerts.length > 0 && (
                  <aside className={styles.alertasNota}>
                    <span className={styles.alertasNotaTag}>
                      <AlertTriangle size={11} aria-hidden="true" /> Revisar antes de cerrar
                    </span>
                    <ul className={styles.alertasNotaLista}>
                      {allAlerts.map((a) => <li key={a}>{a}</li>)}
                    </ul>
                  </aside>
                )}

                {/* La nota es UN solo documento continuo: S·O·A·P·E, luego los
                    recursos del plan y, al final, el resumen que verá el
                    paciente. Sin sub-pestañas: el médico no tiene que recordar
                    dónde vive cada parte de su propia nota. */}
                <NotaSoape
                  soap={localSoap}
                  onChange={handleSoapChange}
                  pendingAIUpdates={pendingAIUpdates}
                  onAccept={acceptAIUpdate}
                  onDismiss={dismissAIUpdate}
                  recentlyUpdated={recentlyUpdated}
                  doctorEdited={doctorEdited}
                />

                <StructuredPlan
                  fhir={fhir}
                  healthEducation={healthEducation}
                  planText={localSoap.p}
                  onRemove={plan.removeItem}
                  onEditMed={plan.editItem}
                  onAddMed={plan.addItem}
                  onEditEducacion={plan.setEducacion}
                />

                {/* ── Resumen para el paciente (cierra la nota) ── */}
                <div className={styles.corteResumen}>
                  <span className={styles.corteResumenEtiqueta}>Para el paciente</span>
                </div>
                {resumenPacienteError && <div className={styles.aiError}>{resumenPacienteError}</div>}
                <section className={styles.resumenPacSec}>
                  <span className={styles.resumenPacInsignia} aria-hidden="true">Rp</span>
                  <div className={styles.resumenPacCuerpo}>
                    <header className={styles.resumenPacHead}>
                      <h4 className={styles.resumenPacTitulo}>Resumen de su consulta</h4>
                      <span className={styles.resumenPacHint}>lo verá en su app</span>
                    </header>
                    <EditableText
                      value={resumenPaciente}
                      onCommit={(texto) => {
                        // A partir de aquí el texto es del médico: la IA no lo
                        // vuelve a pisar salvo que él pulse "Regenerar".
                        resumenEditadoRef.current = true;
                        setResumenPaciente(texto);
                      }}
                      disabled={resumenPacienteLoading}
                      placeholder={resumenPacienteLoading
                        ? 'Generando resumen para el paciente…'
                        : 'Aún no hay resumen. Genérelo con la IA o haga clic aquí para escribirlo.'}
                      ariaLabel="Resumen para el paciente"
                    />
                    <div className={styles.resumenPacPie}>
                      <button
                        type="button"
                        className={styles.resumenPacAccion}
                        onClick={() => generarResumenPaciente({ manual: true })}
                        disabled={resumenPacienteLoading}
                      >
                        {resumenPacienteLoading
                          ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} aria-hidden="true" />
                          : <Sparkles size={12} aria-hidden="true" />}
                        {resumenPaciente ? 'Regenerar con IA' : 'Generar con IA'}
                      </button>
                      <span className={styles.resumenPacNota}>
                        {resumenDesactualizado
                          ? 'El plan cambió después de que usted lo escribió: revíselo o regenérelo.'
                          : 'Se guarda al finalizar la consulta.'}
                      </span>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {/* ── TAB: Paciente ── */}
            {activeTab === 'patient' && (
              <div className={styles.patientTab}>
                <div className={styles.patientHero}>
                  <Avatar className={styles.patientAvatarLg} nombre={patient.nombre} fotoURL={patientFotoURL} zoomable />
                  <div>
                    <h3 className={styles.patientName}>{patient.nombre || 'Cargando...'}</h3>
                    <p className={styles.patientMeta}>
                      {patientContext.age > 0 ? `${patientContext.age} años` : ''}
                      {patientContext.gender ? ` · ${patientContext.gender}` : ''}
                      {cedulaPaciente ? ` · HC-${cedulaPaciente}` : ''}
                    </p>
                  </div>
                </div>

                <div className={styles.motivoCallout}>
                  <div className={styles.motivoCalloutIcon}><ClipboardList size={16} aria-hidden="true" /></div>
                  <div>
                    <p className={styles.motivoLabel}>MOTIVO DE LA CONSULTA</p>
                    <p className={styles.motivoText}>{patient.motivoConsulta || <em className={styles.soapEmpty}>Sin motivo registrado</em>}</p>
                  </div>
                </div>

                <div className={styles.infoSection}>
                  <h5 className={styles.infoSectionTitle}><ShieldAlert size={12} /> Alergias</h5>
                  {patientContext.allergies.length > 0 ? (
                    <div className={styles.tagGroup}>{patientContext.allergies.map((a, i) => <span key={i} className={`${styles.tag} ${styles.tagAllergy}`}>{a}</span>)}</div>
                  ) : (
                    <p className={styles.noAllergyTag}>✓ Sin alergias conocidas</p>
                  )}
                </div>

                {patientContext.medications.length > 0 && (
                  <div className={styles.infoSection}>
                    <h5 className={styles.infoSectionTitle}><Pill size={12} /> Medicación actual</h5>
                    <div className={styles.tagGroup}>{patientContext.medications.map((m, i) => <span key={i} className={`${styles.tag} ${styles.tagMedication}`}>{m}</span>)}</div>
                  </div>
                )}

                {patientContext.conditions.length > 0 && (
                  <div className={styles.infoSection}>
                    <h5 className={styles.infoSectionTitle}><Stethoscope size={12} /> Condiciones</h5>
                    <div className={styles.tagGroup}>{patientContext.conditions.map((c, i) => <span key={i} className={`${styles.tag} ${styles.tagCondition}`}>{c}</span>)}</div>
                  </div>
                )}

                {patientContext.familyHistory.length > 0 && (
                  <div className={styles.infoSection}>
                    <h5 className={styles.infoSectionTitle}><Heart size={12} /> Antecedentes familiares</h5>
                    <div className={styles.tagGroup}>{patientContext.familyHistory.map((h, i) => <span key={i} className={`${styles.tag} ${styles.tagHistoryFamily}`}>{h}</span>)}</div>
                  </div>
                )}

                <div className={styles.infoSection}>
                  <h5 className={styles.infoSectionTitle}><User size={12} /> Datos del paciente</h5>
                  <div className={styles.tagGroup}>
                    {cedulaPaciente && <span className={`${styles.tag} ${styles.tagHistory}`}>Cédula: {cedulaPaciente}</span>}
                    {patientContext.age > 0 && <span className={`${styles.tag} ${styles.tagHistory}`}>{patientContext.age} años</span>}
                  </div>
                </div>
              </div>
            )}

            {/* ── TAB: Expediente ── */}
            {activeTab === 'expediente' && (
              <ExpedienteTab
                pacienteId={firestoreConsulta?.pacienteId}
                pacienteUid={firestoreConsulta?.pacienteUid}
                patientContext={patientContext}
                pacienteFotoURL={patientFotoURL}
                fhir={fhir}
                currentConsultaId={consultaId}
              />
            )}

            {/* ── TAB: Transcripción ── */}
            {activeTab === 'transcript' && (
              <div className={styles.transcriptTab} ref={transcriptionBodyRef}>
                {transcript.length === 0 && !interimText ? (
                  <div className={styles.emptyTabState}>
                    <MessageSquareText size={32} aria-hidden="true" />
                    <h4>Transcripción en vivo</h4>
                    <p>La conversación se transcribirá automáticamente aquí.</p>
                    <p className={styles.txWaiting}>Esperando conversación…</p>
                  </div>
                ) : (
                  <TranscriptThread
                    transcript={transcript}
                    interimText={interimText}
                    interimSpeaker={interimSpeaker}
                    doctorName={doctorName}
                    patientName={patient.nombre}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── COL DERECHA: Copiloto (panel redimensionable y ocultable) ── */}
        {copilotOpen && (
          <>
            <div className={styles.resizeHandleV} onMouseDown={handleCopilotResizeDown} />
            <div className={styles.colCopilot} style={{ width: copilotWidth }}>
              <div className={styles.copilotHeader}>
                <span className={styles.copilotHeaderTitle}>
                  <Stethoscope size={15} aria-hidden="true" /> Asistente médico
                </span>
                <button
                  className={styles.copilotCloseBtn}
                  onClick={() => setCopilotOpen(false)}
                  type="button"
                  title="Ocultar copiloto"
                  aria-label="Ocultar copiloto"
                >
                  <X size={16} aria-hidden="true" />
                </button>
              </div>
              <div className={styles.copilotTab}>
                <div className={styles.copilotFeed} ref={copilotFeedRef}>
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`${styles.chatMsg} ${msg.role === 'ai' ? styles.chatMsgAi : styles.chatMsgUser}`}>
                      <div className={styles.chatMsgAvatar}>
                        {msg.role === 'ai' ? <Stethoscope size={14} /> : <User size={14} />}
                      </div>
                      <div className={styles.chatMsgBody}>
                        <span className={styles.chatMsgRole}>{msg.role === 'ai' ? 'Asistente médico' : 'Tú'}</span>
                        <p className={styles.chatMsgText}>{msg.text}</p>
                      </div>
                    </div>
                  ))}
                  {asistenteBusy && (
                    <div className={`${styles.chatMsg} ${styles.chatMsgAi}`}>
                      <div className={styles.chatMsgAvatar}><Stethoscope size={14} /></div>
                      <div className={styles.chatMsgBody}>
                        <span className={styles.chatMsgRole}>Asistente médico</span>
                        <p className={styles.chatMsgText}>
                          <span className={styles.stopDots} aria-hidden="true"><i /><i /><i /></span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                {copilotVoice.error && (
                  <div className={styles.voiceError}>{copilotVoice.error}</div>
                )}
                <div className={`${styles.composer} ${copilotVoice.listening ? styles.composerListening : ''}`}>
                  <textarea
                    ref={chatInputRef}
                    className={styles.composerInput}
                    value={doctorNoteInput}
                    onChange={e => setDoctorNoteInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendDoctorNote(); } }}
                    placeholder={copilotVoice.listening ? 'Escuchando…' : 'Escribe o dicta una instrucción para la IA…'}
                    rows={1}
                    disabled={asistenteBusy && !copilotVoice.listening}
                    readOnly={copilotVoice.listening}
                  />
                  <div className={styles.composerRow}>
                    <div className={styles.composerRowRight}>
                      {copilotVoice.listening ? (
                        // En dictado: SOLO el botón Detener (sin micrófono ni enviar).
                        <button
                          className={styles.composerStopBtn}
                          onClick={copilotVoice.stop}
                          type="button"
                          title="Detener dictado"
                        >
                          <span className={styles.stopDots} aria-hidden="true"><i /><i /><i /></span>
                          Detener
                        </button>
                      ) : (
                        // En reposo: micrófono + enviar, a la derecha dentro del input.
                        <>
                          {copilotVoice.supported && (
                            <button
                              className={styles.composerIconBtn}
                              onClick={copilotVoice.toggle}
                              disabled={asistenteBusy}
                              type="button"
                              title="Hablar con el asistente"
                              aria-label="Dictar por voz"
                            >
                              <Mic size={18} />
                            </button>
                          )}
                          <button
                            className={styles.composerSendBtn}
                            onClick={handleSendDoctorNote}
                            disabled={!doctorNoteInput.trim() || asistenteBusy}
                            type="button"
                            aria-label="Enviar"
                          >
                            {asistenteBusy ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Botón flotante del Asistente médico (abre el panel del copiloto) */}
      {!copilotOpen && (
        <button
          className={styles.copilotFab}
          onClick={() => setCopilotOpen(true)}
          type="button"
          title="Asistente médico"
          aria-label="Abrir asistente médico"
        >
          <Stethoscope size={24} aria-hidden="true" />
        </button>
      )}

      {/* Finalizando overlay. Mientras aún hay procesamiento de IA pendiente,
          el texto refleja que se está generando la nota (el flush corre al inicio
          de handleHangUp), para que el médico sepa que debe esperar. */}
      {isFinalizando && (
        <div className={styles.finalizandoOverlay}>
          <div className={styles.finalizandoCard}>
            <Loader2 size={32} className={styles.finalizandoSpinner} aria-hidden="true" />
            <h3 className={styles.finalizandoTitle}>Finalizando consulta</h3>
            <p className={styles.finalizandoText}>
              {isProcessing || hasPendingBuffer
                ? 'Generando la nota médica con IA (no cierre esta ventana)…'
                : generandoResumenCierre
                  ? 'Generando el resumen para el paciente (no cierre esta ventana)…'
                  : 'Guardando nota clínica y transcripción…'}
            </p>
          </div>
        </div>
      )}

      {/* 2-minute expiry warning */}
      <Modal
        open={showExpiryWarning}
        onClose={() => { setShowExpiryWarning(false); setExpiryWarningDismissed(true); }}
        title="Tiempo por vencer"
        size="sm"
      >
        <div className={styles.expiryWarningBody}>
          <AlertTriangle size={40} className={styles.expiryWarningIcon} aria-hidden="true" />
          <h3 className={styles.expiryWarningTitle}>
            La consulta termina en {formatRemaining(sessionTimer.remainingSec)}
          </h3>
          <p className={styles.expiryWarningText}>
            {sessionTimer.extensionesRestantes > 0
              ? `Puede extender la sesión (${sessionTimer.extensionesRestantes} ${sessionTimer.extensionesRestantes === 1 ? 'extensión disponible' : 'extensiones disponibles'}) o continuar.`
              : 'No quedan extensiones disponibles.'}
          </p>
          <div className={styles.expiryWarningActions}>
            {sessionTimer.extensionesRestantes > 0 && (
              <Button
                variant="primary"
                onClick={() => {
                  setShowExpiryWarning(false);
                  setExpiryWarningDismissed(true);
                  setShowExtendMenu(true);
                }}
              >
                <TimerReset size={14} aria-hidden="true" /> Extender tiempo
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => { setShowExpiryWarning(false); setExpiryWarningDismissed(true); }}
            >
              Continuar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Paciente no contesta la videollamada — rellamar / finalizar (overlay fijo) */}
      {!modoRevision && esMedico && consultaId && (
        <PacienteNoContestaBanner
          consultaId={consultaId}
          consulta={firestoreConsulta}
          videoStarted={videoStarted}
          onFinalizar={() => {
            // Preseleccionar la disposición correcta según el motivo del banner
            setDisposicionInicialCierre(
              firestoreConsulta?.llamadaRechazada === true
                ? 'paciente_rechazo'
                : 'paciente_no_presento',
            );
            setShowEndCallModal(true);
          }}
        />
      )}

      {/* Close Modal */}
      <CloseModal
        open={showEndCallModal}
        disposicionInicial={disposicionInicialCierre}
        onClose={() => {
          setShowEndCallModal(false);
          setDisposicionInicialCierre(undefined);
        }}
        onConfirm={handleHangUp}
        patientName={patient.nombre}
        duration={formatDuration(callDuration)}
        soapPreview={localSoap}
        fhirCount={fhir.length}
        alertCount={allAlerts.length}
      />

      {/* REC-01: consentimiento HIPAA — gate previo a iniciar el video. */}
      <HIPAAConsentModal
        open={showConsent}
        pacienteNombre={patient.nombre}
        audioGrabado
        onAccept={handleConsentAccept}
        onCancel={() => setShowConsent(false)}
      />
    </div>
  );
}
